import { useState, useEffect, useCallback } from "react";
import PasswordGate from "./PasswordGate";

const BRAND = {
  green:"#4a7c59",greenDark:"#2d5c3a",greenLight:"#7ab894",
  sky:"#87c5d6",skyLight:"#c8e8f0",cream:"#f0ece4",
  charcoal:"#1e2520",muted:"#6b7c72",white:"#ffffff",
  danger:"#c0392b",amber:"#d4890a",success:"#2d7d4f",
};
const GREEN_L = "#E1F5EE";

const CT_API = "https://clinicaltrials.gov/api/v2/studies";

const DISEASE_CATEGORIES = [
  { id:"cancer",      label:"Cancer",               icon:"🎗", term:"cancer" },
  { id:"neuro",       label:"Neurological",         icon:"🧠", term:"alzheimer OR parkinson OR multiple sclerosis OR epilepsy" },
  { id:"heart",       label:"Heart & Cardiovascular",icon:"❤", term:"heart failure OR coronary artery OR cardiovascular" },
  { id:"diabetes",    label:"Diabetes & Metabolic", icon:"🩸", term:"diabetes OR insulin resistance OR metabolic syndrome" },
  { id:"respiratory", label:"Respiratory",          icon:"🫁", term:"asthma OR COPD OR pulmonary fibrosis" },
  { id:"rare",        label:"Rare Diseases",        icon:"🔬", term:"rare disease OR huntington OR ALS OR amyloidosis" },
  { id:"physical",    label:"Physical Disability",  icon:"♿", term:"spinal cord injury OR cerebral palsy OR muscular dystrophy OR limb loss" },
  { id:"mental",      label:"Mental Health",        icon:"🌿", term:"depression OR anxiety OR schizophrenia OR bipolar OR PTSD" },
  { id:"autoimmune",  label:"Autoimmune",           icon:"🛡", term:"rheumatoid arthritis OR lupus OR crohn OR autoimmune" },
  { id:"infectious",  label:"Infectious Disease",   icon:"🦠", term:"HIV OR tuberculosis OR hepatitis OR malaria" },
  { id:"pediatric",   label:"Pediatric",            icon:"👶", term:"pediatric OR childhood cancer OR juvenile arthritis" },
  { id:"aging",       label:"Aging & Longevity",    icon:"🕰", term:"aging OR dementia OR frailty OR longevity" },
  { id:"pain",        label:"Chronic Pain",         icon:"💊", term:"chronic pain OR fibromyalgia OR neuropathic pain" },
  { id:"eye",         label:"Eye & Vision",         icon:"👁", term:"macular degeneration OR glaucoma OR diabetic retinopathy" },
  { id:"kidney",      label:"Kidney Disease",       icon:"🫘", term:"chronic kidney disease OR renal failure" },
  { id:"allergy",     label:"Allergy & Immunology", icon:"🌱", term:"allergic rhinitis OR food allergy OR anaphylaxis OR eczema OR atopic dermatitis OR immunotherapy allergy" },
  { id:"dental",      label:"Dental & Oral Health", icon:"🦷", term:"periodontal disease OR dental caries OR oral cancer OR tooth decay OR gum disease OR oral health" },
];

function parseStudy(s) {
  const ps = s.protocolSection || {};
  const id = ps.identificationModule || {};
  const status = ps.statusModule || {};
  const design = ps.designModule || {};
  const elig = ps.eligibilityModule || {};
  const contacts = ps.contactsLocationsModule || {};
  const desc = ps.descriptionModule || {};
  const sponsor = ps.sponsorCollaboratorsModule || {};

  const nctId = id.nctId || "";
  const overallStatus = status.overallStatus || "UNKNOWN";
  const accepting = overallStatus === "RECRUITING" || overallStatus === "ENROLLING_BY_INVITATION";
  const phases = design.phases || [];
  const phase = phases.length ? phases.join("/").replace(/_/g," ") : "N/A";
  const locs = contacts.locations || [];
  const countries = [...new Set(locs.map(l => l.country).filter(Boolean))];
  const location = countries.length ? countries.slice(0,3).join(", ")+(countries.length>3?` +${countries.length-3}`:"") : "See registry";
  const minAge = elig.minimumAge || "";
  const maxAge = elig.maximumAge || "";
  const ages = minAge||maxAge ? `${minAge||"Any"} – ${maxAge||"Any"}` : "All ages";
  const conditions = ps.conditionsModule?.conditions || [];
  const condition = conditions.slice(0,2).join(", ") || "See registry";
  const leadSponsor = sponsor.leadSponsor?.name || "See registry";
  const briefSummary = desc.briefSummary || "";
  const plainSummary = briefSummary.length > 200 ? briefSummary.slice(0,200).replace(/\s\S*$/,"")+"…" : briefSummary || "See registry for full description.";
  const statusLabel = overallStatus.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
  const statusColor = accepting ? BRAND.success : overallStatus==="NOT_YET_RECRUITING" ? BRAND.amber : BRAND.muted;

  return {
    id:nctId, title:id.briefTitle||id.officialTitle||"Untitled Study",
    phase, accepting, condition, location, ages,
    sponsor:leadSponsor, registry:"ClinicalTrials.gov",
    sourceUrl:`https://clinicaltrials.gov/study/${nctId}`,
    plainSummary, overallStatus, statusLabel, statusColor,
    eligCriteria: elig.eligibilityCriteria || "",
  };
}

const FontStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Sans:wght@300;400;500;600&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    html{scroll-behavior:smooth;}
    body{font-family:'DM Sans',sans-serif;background:${BRAND.cream};color:${BRAND.charcoal};}
    ::-webkit-scrollbar{width:6px;}::-webkit-scrollbar-track{background:${BRAND.cream};}::-webkit-scrollbar-thumb{background:${BRAND.greenLight};border-radius:3px;}
    @keyframes fadeUp{from{opacity:0;transform:translateY(20px);}to{opacity:1;transform:translateY(0);}}
    @keyframes fadeIn{from{opacity:0;}to{opacity:1;}}
    @keyframes spin{to{transform:rotate(360deg);}}
    @keyframes shimmer{0%{opacity:0.4;}50%{opacity:0.8;}100%{opacity:0.4;}}
    .fade-up{animation:fadeUp 0.5s ease both;}.fade-in{animation:fadeIn 0.35s ease both;}
    .delay-1{animation-delay:0.1s;}.delay-2{animation-delay:0.22s;}.delay-3{animation-delay:0.34s;}.delay-4{animation-delay:0.46s;}
    a{color:inherit;text-decoration:none;}button{cursor:pointer;border:none;background:none;font-family:inherit;}
  `}</style>
);

function Logo({ size=40, withText=true, textColor=BRAND.charcoal }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:10}}>
      <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
        <circle cx="40" cy="32" r="26" fill={BRAND.sky}/>
        <path d="M22 20 Q28 14 38 18 Q44 22 42 30 Q38 36 32 34 Q24 30 22 20Z" fill={BRAND.green}/>
        <path d="M46 18 Q56 16 58 26 Q58 34 52 38 Q46 40 44 34 Q42 26 46 18Z" fill={BRAND.green}/>
        <path d="M26 36 Q30 42 28 46 Q24 44 22 38 Q22 34 26 36Z" fill={BRAND.green}/>
        <circle cx="40" cy="32" r="26" fill="none" stroke={BRAND.greenDark} strokeWidth="3"/>
        <ellipse cx="40" cy="28" rx="7" ry="8" fill={BRAND.charcoal}/>
        <path d="M26 52 Q28 40 40 40 Q52 40 54 52Z" fill={BRAND.charcoal}/>
        <path d="M18 54 Q29 49 40 52 Q51 49 62 54 L60 62 Q49 57 40 60 Q31 57 20 62Z" fill={BRAND.charcoal}/>
        <line x1="40" y1="52" x2="40" y2="60" stroke={BRAND.cream} strokeWidth="1.5"/>
      </svg>
      {withText && (
        <div style={{lineHeight:1.1}}>
          <div style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:size*0.42,color:textColor}}>PANGAEA'S</div>
          <div style={{fontFamily:"'DM Sans',sans-serif",fontWeight:500,fontSize:size*0.24,color:BRAND.green,letterSpacing:"0.1em",textTransform:"uppercase"}}>Health & Knowledge</div>
        </div>
      )}
    </div>
  );
}

function Spinner({size=28}) {
  return <div style={{width:size,height:size,border:`2.5px solid ${BRAND.green}25`,borderTop:`2.5px solid ${BRAND.green}`,borderRadius:"50%",animation:"spin 0.7s linear infinite",display:"inline-block"}}/>;
}

function Badge({label,color=BRAND.green}) {
  return <span style={{display:"inline-flex",alignItems:"center",padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:600,letterSpacing:"0.04em",textTransform:"uppercase",color,background:color+"18",border:`1px solid ${color}28`}}>{label}</span>;
}

function Btn({children,onClick,variant="primary",size="md",href,target}) {
  const pad = size==="sm" ? "7px 14px" : "11px 22px";
  const fs = size==="sm" ? 13 : 14;
  const base = {padding:pad,borderRadius:9,fontFamily:"'DM Sans',sans-serif",fontWeight:600,fontSize:fs,transition:"opacity 0.15s, transform 0.15s",display:"inline-flex",alignItems:"center",gap:6};
  const styles = {
    primary:{...base,background:BRAND.green,color:BRAND.white},
    outline:{...base,border:`1.5px solid ${BRAND.green}`,color:BRAND.green,background:"transparent"},
    ghost:{...base,border:`1.5px solid ${BRAND.sky}`,color:BRAND.charcoal,background:"transparent"},
    danger:{...base,background:"#fee2e2",color:BRAND.danger},
  };
  const s = styles[variant]||styles.primary;
  if (href) return <a href={href} target={target} rel="noopener noreferrer" style={s}>{children}</a>;
  return <button onClick={onClick} style={s} onMouseEnter={e=>e.currentTarget.style.opacity="0.82"} onMouseLeave={e=>e.currentTarget.style.opacity="1"}>{children}</button>;
}

function ExternalIcon() {
  return <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
}

function Nav({page,setPage}) {
  const [scrolled,setScrolled] = useState(false);
  const [menuOpen,setMenuOpen] = useState(false);
  useEffect(()=>{const h=()=>setScrolled(window.scrollY>20);window.addEventListener("scroll",h);return()=>window.removeEventListener("scroll",h);},[]);
  const links=[{id:"home",label:"Home"},{id:"search",label:"Find Trials"},{ id: "how-trials-work", label: "How trials work" },{id:"compare",label:"Compare"},{id:"profile",label:"My Profile"},{id:"community",label:"Community"},{id:"updates",label:"Updates"},{id:"about",label:"About Us"}];
  const navBg = scrolled||menuOpen ? "rgba(240,236,228,0.97)" : "transparent";
  return (
    <nav style={{position:"fixed",top:0,left:0,right:0,zIndex:100,background:navBg,backdropFilter:scrolled||menuOpen?"blur(12px)":"none",borderBottom:scrolled||menuOpen?`1px solid rgba(74,124,89,0.15)`:"none",transition:"all 0.3s",padding:"0 clamp(16px,4vw,48px)"}}>
      <div style={{maxWidth:1200,margin:"0 auto",height:70,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <button onClick={()=>{setPage("home");setMenuOpen(false);}}><Logo size={38}/></button>
        {/* Desktop links */}
        <div style={{display:"flex",gap:4,alignItems:"center"}} className="phk-desktop-nav">
          {links.map(l=>(
            <button key={l.id} onClick={()=>setPage(l.id)} style={{padding:"8px 14px",borderRadius:8,fontWeight:500,fontSize:14,color:page===l.id?BRAND.white:BRAND.charcoal,background:page===l.id?BRAND.green:"transparent",transition:"all 0.15s",whiteSpace:"nowrap"}}>{l.label}</button>
          ))}
        </div>
        {/* Hamburger — mobile only */}
        <button onClick={()=>setMenuOpen(o=>!o)} className="phk-hamburger" style={{display:"none",flexDirection:"column",gap:5,padding:8,borderRadius:8,background:"transparent",border:"none",cursor:"pointer"}}>
          <span style={{display:"block",width:22,height:2,background:BRAND.charcoal,borderRadius:2,transition:"all 0.22s",transform:menuOpen?"rotate(45deg) translate(5px,5px)":"none"}}/>
          <span style={{display:"block",width:22,height:2,background:BRAND.charcoal,borderRadius:2,transition:"all 0.22s",opacity:menuOpen?0:1}}/>
          <span style={{display:"block",width:22,height:2,background:BRAND.charcoal,borderRadius:2,transition:"all 0.22s",transform:menuOpen?"rotate(-45deg) translate(5px,-5px)":"none"}}/>
        </button>
      </div>
      {/* Mobile dropdown */}
      {menuOpen&&(
        <div style={{background:"rgba(240,236,228,0.97)",backdropFilter:"blur(12px)",borderTop:"1px solid rgba(74,124,89,0.12)",padding:"10px 16px 18px"}}>
          {links.map(l=>(
            <button key={l.id} onClick={()=>{setPage(l.id);setMenuOpen(false);}}
              style={{display:"block",width:"100%",textAlign:"left",padding:"11px 14px",borderRadius:9,fontWeight:600,fontSize:15,color:page===l.id?BRAND.white:BRAND.charcoal,background:page===l.id?BRAND.green:"transparent",marginBottom:3,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:"pointer"}}>
              {l.label}
            </button>
          ))}
        </div>
      )}
      <style>{`
        @media(max-width:800px){
          .phk-desktop-nav{display:none!important;}
          .phk-hamburger{display:flex!important;}
        }
      `}</style>
    </nav>
  );
}

function TrialCard({trial,onView,onCompare,inComparison,onRemove}) {
  return (
    <div style={{background:BRAND.white,borderRadius:16,border:inComparison?`2px solid ${BRAND.green}`:`1px solid rgba(74,124,89,0.14)`,padding:"20px 22px",display:"flex",flexDirection:"column",gap:12,transition:"transform 0.2s,box-shadow 0.2s",boxShadow:inComparison?`0 0 0 3px ${BRAND.green}18`:"0 2px 8px rgba(0,0,0,0.05)"}}
      onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,0.1)";}}
      onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow=inComparison?`0 0 0 3px ${BRAND.green}18`:"0 2px 8px rgba(0,0,0,0.05)";}}
    >
      <div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
          <Badge label={trial.phase} color={BRAND.green}/>
          <Badge label={trial.statusLabel} color={trial.statusColor}/>
          {trial.accepting && <Badge label="Open now" color={BRAND.success}/>}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
          <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:14,fontWeight:700,lineHeight:1.35,color:BRAND.charcoal,flex:1}}>{trial.title}</h3>
          <span style={{fontSize:10,color:BRAND.muted,whiteSpace:"nowrap",marginTop:2}}>{trial.id}</span>
        </div>
      </div>
      <p style={{fontSize:12,color:BRAND.muted,lineHeight:1.65}}>{trial.plainSummary}</p>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
        {[["Condition",trial.condition],["Location",trial.location],["Ages",trial.ages],["Sponsor",trial.sponsor.length>30?trial.sponsor.slice(0,30)+"…":trial.sponsor]].map(([k,v])=>(
          <div key={k} style={{background:BRAND.cream,borderRadius:7,padding:"7px 9px"}}>
            <div style={{fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:2}}>{k}</div>
            <div style={{fontSize:12,color:BRAND.charcoal,fontWeight:500}}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:7,paddingTop:2}}>
        <Btn onClick={()=>onView(trial)} size="sm" style={{flex:1}}>View Details</Btn>
        <Btn href={trial.sourceUrl} target="_blank" variant="outline" size="sm"><ExternalIcon/> Source</Btn>
        {inComparison
          ? <Btn onClick={()=>onRemove(trial.id)} variant="danger" size="sm">✕</Btn>
          : <Btn onClick={()=>onCompare(trial)} variant="ghost" size="sm">+ Compare</Btn>
        }
      </div>
    </div>
  );
}

function HomePage({setPage}) {
  return (
    <div>
      <div style={{minHeight:"100vh",background:`linear-gradient(155deg,${BRAND.greenDark} 0%,${BRAND.green} 55%,${BRAND.sky} 100%)`,display:"flex",flexDirection:"column",justifyContent:"center",alignItems:"center",padding:"120px clamp(16px,5vw,80px) 80px",textAlign:"center",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-80,right:-80,width:400,height:400,borderRadius:"50%",background:"rgba(255,255,255,0.05)",pointerEvents:"none"}}/>
        <div style={{position:"absolute",bottom:-60,left:-60,width:300,height:300,borderRadius:"50%",background:"rgba(255,255,255,0.04)",pointerEvents:"none"}}/>
        <div className="fade-up" style={{marginBottom:28}}><Logo size={72} textColor={BRAND.white}/></div>
        <h1 className="fade-up delay-1" style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:"clamp(34px,6vw,68px)",color:BRAND.white,lineHeight:1.1,maxWidth:800,marginBottom:20}}>Global Clinical Trials,<br/>Made Human</h1>
        <p className="fade-up delay-2" style={{fontSize:"clamp(15px,2vw,18px)",color:"rgba(255,255,255,0.85)",maxWidth:560,lineHeight:1.75,marginBottom:40}}>Real data from ClinicalTrials.gov — hundreds of thousands of trials across cancer, neurological disease, rare conditions, physical disability, and far more — translated into plain language for patients and caregivers.</p>
        <div className="fade-up delay-3" style={{display:"flex",gap:14,flexWrap:"wrap",justifyContent:"center",marginBottom:60}}>
          <button onClick={()=>setPage("search")} style={{padding:"16px 36px",borderRadius:10,background:BRAND.white,color:BRAND.greenDark,fontWeight:700,fontSize:16,fontFamily:"'DM Sans',sans-serif",boxShadow:"0 4px 16px rgba(0,0,0,0.15)",transition:"transform 0.2s,box-shadow 0.2s"}} onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,0.2)";}} onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.15)";}}>Find Trials for Me</button>
          <button onClick={()=>setPage("about")} style={{padding:"16px 36px",borderRadius:10,background:"transparent",color:BRAND.white,fontWeight:600,fontSize:16,fontFamily:"'DM Sans',sans-serif",border:"2px solid rgba(255,255,255,0.5)",transition:"background 0.2s"}} onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>Our Story</button>
        </div>
        <div className="fade-up delay-4" style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:14,width:"100%",maxWidth:680}}>
          {[["450,000+","Real trials"],["15+","Disease categories"],["190+","Countries"],["Free","Always"]].map(([n,l])=>(
            <div key={n} style={{background:"rgba(255,255,255,0.12)",backdropFilter:"blur(8px)",borderRadius:12,padding:"18px 14px",border:"1px solid rgba(255,255,255,0.2)"}}>
              <div style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:26,color:BRAND.white}}>{n}</div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.7)",marginTop:4,fontWeight:500}}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{maxWidth:1100,margin:"0 auto",padding:"80px clamp(16px,4vw,48px) 60px"}}>
        <div style={{textAlign:"center",marginBottom:48}}>
          <div style={{fontWeight:600,fontSize:12,textTransform:"uppercase",letterSpacing:"0.1em",color:BRAND.green,marginBottom:10}}>Disease categories</div>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:"clamp(26px,4vw,40px)",color:BRAND.charcoal}}>Search across every major condition</h2>
          <p style={{fontSize:15,color:BRAND.muted,marginTop:12,maxWidth:520,margin:"12px auto 0",lineHeight:1.7}}>Click any category to search real, live trials from ClinicalTrials.gov.</p>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(148px,1fr))",gap:12}}>
          {DISEASE_CATEGORIES.map(cat=>(
            <button key={cat.id} onClick={()=>setPage("search")} style={{background:BRAND.white,border:`1px solid rgba(74,124,89,0.15)`,borderRadius:12,padding:"18px 14px",textAlign:"center",cursor:"pointer",transition:"all 0.2s",fontFamily:"'DM Sans',sans-serif"}}
              onMouseEnter={e=>{e.currentTarget.style.background=BRAND.green;e.currentTarget.style.color=BRAND.white;e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow="0 6px 20px rgba(0,0,0,0.1)";}}
              onMouseLeave={e=>{e.currentTarget.style.background=BRAND.white;e.currentTarget.style.color=BRAND.charcoal;e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="none";}}
            >
              <div style={{fontSize:24,marginBottom:8}}>{cat.icon}</div>
              <div style={{fontSize:12,fontWeight:600}}>{cat.label}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={{background:BRAND.charcoal,padding:"72px clamp(16px,4vw,48px)",textAlign:"center"}}>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:"clamp(22px,3vw,36px)",color:BRAND.white,marginBottom:14}}>Even if no trial is open today —<br/>hope is being built right now</h2>
        <p style={{fontSize:15,color:"rgba(255,255,255,0.6)",maxWidth:520,margin:"0 auto 28px",lineHeight:1.75}}>Follow any trial at any phase. When it opens or advances, we'll let you know.</p>
        <button onClick={()=>setPage("search")} style={{padding:"14px 32px",borderRadius:10,background:BRAND.green,color:BRAND.white,fontWeight:700,fontSize:15,fontFamily:"'DM Sans',sans-serif"}}>Start Searching</button>
      </div>
    </div>
  );
}

function SearchPage({comparison,setComparison,setPage,setDetailTrial}) {
  const [q,setQ]                   = useState("");
  const [debouncedQ,setDebouncedQ] = useState("");
  const [activeCat,setActiveCat]   = useState(null);
  const [trials,setTrials]         = useState([]);
  const [loading,setLoading]       = useState(false);
  const [error,setError]           = useState(null);
  const [totalCount,setTotalCount] = useState(0);
  const [nextToken,setNextToken]   = useState(null);
  const [hasMore,setHasMore]       = useState(false);
  const [filtersOpen,setFiltersOpen] = useState(false);

  // ── Filter state ────────────────────────────────────────────
  const [statusFilter,setStatusFilter]     = useState([]);   // multi-select
  const [phaseFilter,setPhaseFilter]       = useState([]);   // multi-select
  const [sexFilter,setSexFilter]           = useState("ALL");
  const [minAge,setMinAge]                 = useState(0);
  const [maxAge,setMaxAge]                 = useState(100);
  const [countryFilter,setCountryFilter]   = useState("");
  const [interventionFilter,setIntervention] = useState("");
  const [resultsOnly,setResultsOnly]       = useState(false);
  const [sponsorFilter,setSponsorFilter]   = useState("");

  const PAGE_SIZE = 20;

  const STATUS_OPTIONS = [
    { value:"RECRUITING",              label:"Recruiting",              color:BRAND.success },
    { value:"NOT_YET_RECRUITING",      label:"Not yet recruiting",      color:BRAND.amber   },
    { value:"COMPLETED",               label:"Completed",               color:"#185fa5"     },
    { value:"ACTIVE_NOT_RECRUITING",   label:"Active, not recruiting",  color:BRAND.muted   },
    { value:"ENROLLING_BY_INVITATION", label:"Enrolling by invitation", color:BRAND.green   },
    { value:"SUSPENDED",               label:"Suspended / Terminated",  color:BRAND.danger  },
  ];

  const PHASE_OPTIONS = [
    "PHASE1","PHASE2","PHASE3","PHASE4","EARLY_PHASE1","NA"
  ];
  const PHASE_LABELS = {
    PHASE1:"Phase 1", PHASE2:"Phase 2", PHASE3:"Phase 3",
    PHASE4:"Phase 4", EARLY_PHASE1:"Early Phase 1", NA:"N/A"
  };

  const INTERVENTION_OPTIONS = [
    {value:"",label:"Any type"},
    {value:"DRUG",label:"Drug"},
    {value:"BIOLOGICAL",label:"Biological"},
    {value:"DEVICE",label:"Device"},
    {value:"BEHAVIORAL",label:"Behavioral"},
    {value:"PROCEDURE",label:"Procedure"},
    {value:"DIETARY_SUPPLEMENT",label:"Dietary supplement"},
    {value:"OTHER",label:"Other"},
  ];

  const COUNTRIES = [
    "","United States","United Kingdom","Canada","Germany","France",
    "Australia","Japan","China","India","Brazil","Spain","Italy",
    "Netherlands","Sweden","Switzerland","South Korea","Israel","Norway","Denmark",
  ];

  // Count active filters for the badge
  const activeFilterCount = [
    statusFilter.length > 0,
    phaseFilter.length > 0,
    sexFilter !== "ALL",
    minAge > 0 || maxAge < 100,
    countryFilter !== "",
    interventionFilter !== "",
    resultsOnly,
    sponsorFilter.trim() !== "",
  ].filter(Boolean).length;

  const toggleStatus = (v) => setStatusFilter(prev=>prev.includes(v)?prev.filter(x=>x!==v):[...prev,v]);
  const togglePhase  = (v) => setPhaseFilter(prev=>prev.includes(v)?prev.filter(x=>x!==v):[...prev,v]);

  const clearFilters = () => {
    setStatusFilter([]); setPhaseFilter([]); setSexFilter("ALL");
    setMinAge(0); setMaxAge(100); setCountryFilter("");
    setIntervention(""); setResultsOnly(false); setSponsorFilter("");
  };

  // Debounce search
  useEffect(()=>{const t=setTimeout(()=>setDebouncedQ(q),500);return()=>clearTimeout(t);},[q]);

  const fetchTrials = useCallback(async(searchQ,catId,pageToken,append,overrides={})=>{
    setLoading(true); setError(null);
    try {
      const cat = DISEASE_CATEGORIES.find(c=>c.id===catId);
      const condQuery = searchQ.trim() || (cat?cat.term:"disease OR cancer OR diabetes OR neurological");

      const params = new URLSearchParams({
        format:"json", pageSize:PAGE_SIZE,
        "query.cond": condQuery,
        countTotal:"true",
        fields:["NCTId","BriefTitle","OfficialTitle","OverallStatus","Phase","Condition",
                "LocationCountry","MinimumAge","MaximumAge","LeadSponsorName",
                "BriefSummary","EligibilityModule","InterventionType","HasResults"].join("|"),
      });

      // Status filter
      const st = overrides.statusFilter ?? statusFilter;
      if (st.length > 0) params.set("filter.overallStatus", st.join(","));

      // Build all filter.advanced parts into an array — avoids the
      // repeated params.set() overwrite bug and fixes the phase filter
      const advancedParts = [];

      // Sex filter
      const sx = overrides.sexFilter ?? sexFilter;
      if (sx !== "ALL") advancedParts.push(`AREA[EligibilitySex]${sx}`);

      // Phase filter — filter.phase is NOT a valid CT.gov v2 param.
      // Correct syntax: AREA[Phase] inside filter.advanced
      const ph = overrides.phaseFilter ?? phaseFilter;
      if (ph.length > 0) {
        advancedParts.push("(" + ph.map(p=>`AREA[Phase]${p}`).join(" OR ") + ")");
      }

      // Intervention type
      const iv = overrides.interventionFilter ?? interventionFilter;
      if (iv) advancedParts.push(`AREA[InterventionType]${iv}`);

      // Results only
      const ro = overrides.resultsOnly ?? resultsOnly;
      if (ro) advancedParts.push("AREA[ResultsFirstSubmitDate]RANGE[2000-01-01, MAX]");

      // Age (server-side assist)
      const mn = overrides.minAge ?? minAge;
      const mx = overrides.maxAge ?? maxAge;
      if (mn > 0) advancedParts.push(`AREA[MinimumAge]RANGE[MIN, ${mx} years]`);

      if (advancedParts.length > 0) {
        params.set("filter.advanced", advancedParts.join(" AND "));
      }

      if (pageToken) params.set("pageToken", pageToken);

      const res  = await fetch(`${CT_API}?${params}`);
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();

      let parsed = (data.studies||[]).map(parseStudy);

      // Client-side age filter as a safety net
      // Strip non-numeric chars before parseInt so "18 Years" → 18
      if (mn > 0 || mx < 100) {
        parsed = parsed.filter(t=>{
          if (!t.ages || t.ages === "All ages") return true;
          const parts = t.ages.split("–");
          const tMin = parseInt((parts[0]||"").replace(/\D/g,"")) || 0;
          const tMax = parseInt((parts[1]||"").replace(/\D/g,"")) || 999;
          return tMin <= mx && tMax >= mn;
        });
      }

      // Client-side country filter
      const ct = overrides.countryFilter ?? countryFilter;
      if (ct) parsed = parsed.filter(t=>t.location.toLowerCase().includes(ct.toLowerCase()));

      // Client-side sponsor filter
      const sp = overrides.sponsorFilter ?? sponsorFilter;
      if (sp.trim()) parsed = parsed.filter(t=>t.sponsor.toLowerCase().includes(sp.trim().toLowerCase()));

      setTrials(prev=>append?[...prev,...parsed]:parsed);
      setTotalCount(data.totalCount||0);
      setNextToken(data.nextPageToken||null);
      setHasMore(!!data.nextPageToken);
    } catch(err) {
      setError("Could not load trials from ClinicalTrials.gov. Please check your connection and try again.");
      console.error(err);
    } finally { setLoading(false); }
  },[statusFilter,phaseFilter,sexFilter,minAge,maxAge,countryFilter,interventionFilter,resultsOnly,sponsorFilter]);

  useEffect(()=>{ fetchTrials("",null,null,false); },[]);
  useEffect(()=>{ setNextToken(null); fetchTrials(debouncedQ,activeCat,null,false); },[debouncedQ,activeCat,statusFilter,phaseFilter,sexFilter,minAge,maxAge,countryFilter,interventionFilter,resultsOnly,sponsorFilter]);

  const addCompare=(t)=>{ if(!comparison.find(c=>c.id===t.id)&&comparison.length<4) setComparison([...comparison,t]); };
  const removeCompare=(id)=>setComparison(comparison.filter(c=>c.id!==id));

  const pill = (label,active,onClick,color=BRAND.green) => (
    <button key={label} onClick={onClick} style={{padding:"5px 12px",borderRadius:20,fontSize:12,fontWeight:600,fontFamily:"'DM Sans',sans-serif",background:active?color:BRAND.white,color:active?BRAND.white:BRAND.charcoal,border:`1.5px solid ${active?color:"rgba(74,124,89,0.2)"}`,cursor:"pointer",transition:"all 0.15s",whiteSpace:"nowrap"}}>
      {label}
    </button>
  );

  const slab = {background:BRAND.white,borderRadius:12,padding:"16px 18px",border:`0.5px solid rgba(74,124,89,0.15)`,marginBottom:0};
  const filterLabel = {display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",color:BRAND.muted,marginBottom:8};

  return (
    <div style={{paddingTop:90}}>
      {/* Search hero */}
      <div style={{background:BRAND.greenDark,padding:"56px clamp(16px,4vw,48px) 44px"}}>
        <div style={{maxWidth:780,margin:"0 auto",textAlign:"center"}}>
          <h1 className="fade-up" style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:"clamp(26px,4vw,42px)",color:BRAND.white,marginBottom:10}}>Find Your Trial</h1>
          <p className="fade-up delay-1" style={{color:"rgba(255,255,255,0.72)",fontSize:15,marginBottom:28}}>
            Live data from ClinicalTrials.gov{totalCount>0?` — ${totalCount.toLocaleString()} trials found`:""}
          </p>
          <div className="fade-up delay-2" style={{position:"relative"}}>
            <input value={q} onChange={e=>setQ(e.target.value)}
              placeholder="Search by condition, e.g. breast cancer, ALS, spinal cord injury, depression…"
              style={{width:"100%",padding:"15px 20px 15px 46px",borderRadius:12,border:"none",fontSize:15,fontFamily:"'DM Sans',sans-serif",background:BRAND.white,color:BRAND.charcoal,outline:"none",boxShadow:"0 4px 20px rgba(0,0,0,0.15)"}}/>
            <svg style={{position:"absolute",left:16,top:"50%",transform:"translateY(-50%)",color:BRAND.muted}} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            {loading&&q&&<div style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)"}}><Spinner size={20}/></div>}
          </div>
        </div>
      </div>

      <div style={{maxWidth:1200,margin:"0 auto",padding:"24px clamp(16px,4vw,48px)"}}>

        {/* Disease category pills */}
        <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:14}}>
          <button onClick={()=>setActiveCat(null)} style={{padding:"6px 13px",borderRadius:20,fontSize:12,fontWeight:600,fontFamily:"'DM Sans',sans-serif",background:!activeCat?BRAND.green:BRAND.white,color:!activeCat?BRAND.white:BRAND.charcoal,border:`1.5px solid ${!activeCat?BRAND.green:"rgba(74,124,89,0.2)"}`,transition:"all 0.15s"}}>All conditions</button>
          {DISEASE_CATEGORIES.map(cat=>(
            <button key={cat.id} onClick={()=>setActiveCat(activeCat===cat.id?null:cat.id)}
              style={{padding:"6px 13px",borderRadius:20,fontSize:12,fontWeight:600,fontFamily:"'DM Sans',sans-serif",background:activeCat===cat.id?BRAND.green:BRAND.white,color:activeCat===cat.id?BRAND.white:BRAND.charcoal,border:`1.5px solid ${activeCat===cat.id?BRAND.green:"rgba(74,124,89,0.2)"}`,transition:"all 0.15s"}}>
              {cat.icon} {cat.label}
            </button>
          ))}
        </div>

        {/* Filter toggle bar */}
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
          <button onClick={()=>setFiltersOpen(o=>!o)}
            style={{display:"flex",alignItems:"center",gap:7,padding:"8px 16px",borderRadius:9,border:`1.5px solid ${filtersOpen||activeFilterCount>0?BRAND.green:"rgba(74,124,89,0.25)"}`,background:filtersOpen||activeFilterCount>0?BRAND.green+"12":BRAND.white,color:activeFilterCount>0?BRAND.greenDark:BRAND.charcoal,fontWeight:600,fontSize:13,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",transition:"all 0.15s"}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
            Filters
            {activeFilterCount>0 && <span style={{background:BRAND.green,color:BRAND.white,borderRadius:"50%",width:18,height:18,fontSize:10,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>{activeFilterCount}</span>}
          </button>
          {activeFilterCount>0 && (
            <button onClick={clearFilters} style={{fontSize:12,color:BRAND.muted,fontWeight:500,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",textDecoration:"underline"}}>
              Clear all filters
            </button>
          )}
          {totalCount>0&&!loading&&(
            <span style={{fontSize:13,color:BRAND.muted,marginLeft:"auto"}}>
              Showing {trials.length.toLocaleString()} of {totalCount.toLocaleString()} trials
            </span>
          )}
        </div>

        {/* Filter panel */}
        {filtersOpen && (
          <div style={{background:BRAND.cream,borderRadius:14,padding:"20px 22px",marginBottom:20,border:`1px solid rgba(74,124,89,0.18)`}}>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:14}}>

              {/* Status */}
              <div style={slab}>
                <label style={filterLabel}>Trial status</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {STATUS_OPTIONS.map(s=>pill(s.label,statusFilter.includes(s.value),()=>toggleStatus(s.value),s.color))}
                </div>
              </div>

              {/* Phase */}
              <div style={slab}>
                <label style={filterLabel}>Trial phase</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {PHASE_OPTIONS.map(p=>pill(PHASE_LABELS[p],phaseFilter.includes(p),()=>togglePhase(p)))}
                </div>
              </div>

              {/* Sex */}
              <div style={slab}>
                <label style={filterLabel}>Sex / gender eligibility</label>
                <div style={{display:"flex",gap:6}}>
                  {[["ALL","All"],["FEMALE","Female"],["MALE","Male"]].map(([v,l])=>pill(l,sexFilter===v,()=>setSexFilter(v)))}
                </div>
              </div>

              {/* Age range */}
              <div style={slab}>
                <label style={filterLabel}>Age range — {minAge} to {maxAge} years</label>
                <div style={{display:"flex",gap:12,alignItems:"center"}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:BRAND.muted,marginBottom:4}}>Min age</div>
                    <input type="range" min={0} max={100} step={1} value={minAge}
                      onChange={e=>setMinAge(Number(e.target.value))}
                      style={{width:"100%",accentColor:BRAND.green}}/>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:11,color:BRAND.muted,marginBottom:4}}>Max age</div>
                    <input type="range" min={0} max={100} step={1} value={maxAge}
                      onChange={e=>setMaxAge(Number(e.target.value))}
                      style={{width:"100%",accentColor:BRAND.green}}/>
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
                  <span style={{fontSize:12,color:BRAND.muted}}>Min: <strong style={{color:BRAND.charcoal}}>{minAge}</strong></span>
                  <span style={{fontSize:12,color:BRAND.muted}}>Max: <strong style={{color:BRAND.charcoal}}>{maxAge}</strong></span>
                </div>
              </div>

              {/* Country */}
              <div style={slab}>
                <label style={filterLabel}>Country / region</label>
                <select value={countryFilter} onChange={e=>setCountryFilter(e.target.value)}
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1.5px solid rgba(74,124,89,0.2)",fontSize:13,fontFamily:"'DM Sans',sans-serif",color:BRAND.charcoal,background:BRAND.white,outline:"none",cursor:"pointer"}}>
                  <option value="">Any country</option>
                  {COUNTRIES.filter(c=>c).map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Intervention type */}
              <div style={slab}>
                <label style={filterLabel}>Intervention type</label>
                <select value={interventionFilter} onChange={e=>setIntervention(e.target.value)}
                  style={{width:"100%",padding:"9px 12px",borderRadius:8,border:"1.5px solid rgba(74,124,89,0.2)",fontSize:13,fontFamily:"'DM Sans',sans-serif",color:BRAND.charcoal,background:BRAND.white,outline:"none",cursor:"pointer"}}>
                  {INTERVENTION_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              {/* Results available */}
              <div style={slab}>
                <label style={filterLabel}>Results</label>
                <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",fontSize:13,color:BRAND.charcoal,fontWeight:500}}>
                  <input type="checkbox" checked={resultsOnly} onChange={e=>setResultsOnly(e.target.checked)}
                    style={{accentColor:BRAND.green,width:16,height:16}}/>
                  Show only trials with posted results
                </label>
                <p style={{fontSize:11,color:BRAND.muted,marginTop:6,lineHeight:1.5}}>Useful for seeing what outcomes were found in completed trials.</p>
              </div>

              {/* Sponsor search */}
              <div style={slab}>
                <label style={filterLabel}>Sponsor / organisation</label>
                <div style={{position:"relative"}}>
                  <input
                    type="text"
                    value={sponsorFilter}
                    onChange={e=>setSponsorFilter(e.target.value)}
                    placeholder="e.g. NIH, Pfizer, Johns Hopkins…"
                    style={{width:"100%",padding:"9px 34px 9px 12px",borderRadius:8,border:`1.5px solid ${sponsorFilter?BRAND.green:"rgba(74,124,89,0.2)"}`,fontSize:13,fontFamily:"'DM Sans',sans-serif",color:BRAND.charcoal,background:BRAND.white,outline:"none"}}
                    onFocus={e=>e.target.style.borderColor=BRAND.green}
                    onBlur={e=>e.target.style.borderColor=sponsorFilter?"rgba(74,124,89,0.5)":"rgba(74,124,89,0.2)"}
                  />
                  {sponsorFilter && (
                    <button onClick={()=>setSponsorFilter("")}
                      style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",cursor:"pointer",color:BRAND.muted,fontSize:14,lineHeight:1,padding:"0 2px"}}>
                      ✕
                    </button>
                  )}
                </div>
                <p style={{fontSize:11,color:BRAND.muted,marginTop:6,lineHeight:1.5}}>Filter by the organisation running the trial — a university, hospital, biotech, or government agency.</p>
              </div>

            </div>

            {/* Active filter summary */}
            {activeFilterCount>0 && (
              <div style={{marginTop:14,paddingTop:14,borderTop:`0.5px solid rgba(74,124,89,0.15)`,display:"flex",flexWrap:"wrap",gap:6,alignItems:"center"}}>
                <span style={{fontSize:11,color:BRAND.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em"}}>Active:</span>
                {statusFilter.map(s=><span key={s} style={{fontSize:11,background:BRAND.green+"18",color:BRAND.greenDark,padding:"2px 8px",borderRadius:20,fontWeight:600}}>{STATUS_OPTIONS.find(o=>o.value===s)?.label}</span>)}
                {phaseFilter.map(p=><span key={p} style={{fontSize:11,background:BRAND.green+"18",color:BRAND.greenDark,padding:"2px 8px",borderRadius:20,fontWeight:600}}>{PHASE_LABELS[p]}</span>)}
                {sexFilter!=="ALL" && <span style={{fontSize:11,background:BRAND.green+"18",color:BRAND.greenDark,padding:"2px 8px",borderRadius:20,fontWeight:600}}>{sexFilter==="FEMALE"?"Female only":"Male only"}</span>}
                {(minAge>0||maxAge<100) && <span style={{fontSize:11,background:BRAND.green+"18",color:BRAND.greenDark,padding:"2px 8px",borderRadius:20,fontWeight:600}}>Age {minAge}–{maxAge}</span>}
                {countryFilter && <span style={{fontSize:11,background:BRAND.green+"18",color:BRAND.greenDark,padding:"2px 8px",borderRadius:20,fontWeight:600}}>{countryFilter}</span>}
                {interventionFilter && <span style={{fontSize:11,background:BRAND.green+"18",color:BRAND.greenDark,padding:"2px 8px",borderRadius:20,fontWeight:600}}>{INTERVENTION_OPTIONS.find(o=>o.value===interventionFilter)?.label}</span>}
                {resultsOnly && <span style={{fontSize:11,background:BRAND.green+"18",color:BRAND.greenDark,padding:"2px 8px",borderRadius:20,fontWeight:600}}>Has results</span>}
                {sponsorFilter.trim() && <span style={{fontSize:11,background:BRAND.green+"18",color:BRAND.greenDark,padding:"2px 8px",borderRadius:20,fontWeight:600}}>Sponsor: {sponsorFilter.trim()}</span>}
              </div>
            )}
          </div>
        )}

        {/* Comparison bar */}
        {comparison.length>0&&(
          <div style={{background:BRAND.green+"14",border:`1.5px solid ${BRAND.green}`,borderRadius:12,padding:"12px 18px",marginBottom:20,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{fontWeight:600,fontSize:13,color:BRAND.greenDark}}>Comparing {comparison.length}/4 trials</span>
            {comparison.map(t=><Badge key={t.id} label={t.id} color={BRAND.green}/>)}
            <button onClick={()=>setPage("compare")} style={{marginLeft:"auto",padding:"7px 16px",borderRadius:8,background:BRAND.green,color:BRAND.white,fontWeight:600,fontSize:13,fontFamily:"'DM Sans',sans-serif"}}>View Comparison →</button>
          </div>
        )}

        {error&&(
          <div style={{background:"#fee2e2",border:`1px solid ${BRAND.danger}30`,borderRadius:12,padding:"16px 20px",marginBottom:20,color:BRAND.danger,fontSize:14}}>{error}</div>
        )}

        {/* Skeleton loading */}
        {loading&&trials.length===0&&(
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:18}}>
            {[...Array(8)].map((_,i)=>(
              <div key={i} style={{background:BRAND.white,borderRadius:16,border:`1px solid rgba(74,124,89,0.1)`,padding:"20px 22px",height:260}}>
                {[70,140,100,60].map((w,j)=>(
                  <div key={j} style={{height:11,borderRadius:6,background:BRAND.cream,width:`${w}%`,marginBottom:14,animation:`shimmer 1.4s ease ${j*0.15}s infinite`}}/>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Results grid */}
        {trials.length>0&&(
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:18}}>
            {trials.map(t=>(
              <TrialCard key={t.id} trial={t}
                onView={trial=>{setDetailTrial(trial);setPage("detail");}}
                onCompare={addCompare}
                inComparison={!!comparison.find(c=>c.id===t.id)}
                onRemove={removeCompare}
              />
            ))}
          </div>
        )}

        {loading&&trials.length>0&&(
          <div style={{display:"flex",justifyContent:"center",padding:"32px 0"}}><Spinner size={36}/></div>
        )}

        {!loading&&hasMore&&(
          <div style={{display:"flex",justifyContent:"center",padding:"36px 0"}}>
            <button onClick={()=>fetchTrials(debouncedQ,activeCat,nextToken,true)}
              style={{padding:"13px 36px",borderRadius:10,background:BRAND.green,color:BRAND.white,fontWeight:600,fontSize:15,fontFamily:"'DM Sans',sans-serif"}}>
              Load more trials
            </button>
          </div>
        )}

        {!loading&&trials.length===0&&!error&&(
          <div style={{textAlign:"center",padding:"60px 20px",color:BRAND.muted}}>
            <div style={{fontSize:40,marginBottom:12}}>🔍</div>
            <p style={{fontSize:15}}>No trials found. Try a different search term or adjust your filters.</p>
            {activeFilterCount>0&&<button onClick={clearFilters} style={{marginTop:12,color:BRAND.green,fontWeight:600,fontSize:14,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",textDecoration:"underline"}}>Clear all filters</button>}
          </div>
        )}
      </div>
    </div>
  );
}

function DetailPage({trial,setPage}) {
  if (!trial) return (
    <div style={{padding:"160px 40px",textAlign:"center"}}>
      <p style={{color:BRAND.muted,marginBottom:16}}>No trial selected.</p>
      <button onClick={()=>setPage("search")} style={{color:BRAND.green,fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>← Back to search</button>
    </div>
  );
  return (
    <div style={{paddingTop:90}}>
      <div style={{background:BRAND.greenDark,padding:"48px clamp(16px,4vw,48px) 40px"}}>
        <div style={{maxWidth:900,margin:"0 auto"}}>
          <button onClick={()=>setPage("search")} style={{color:"rgba(255,255,255,0.7)",fontSize:13,fontWeight:500,marginBottom:16,display:"flex",alignItems:"center",gap:6,fontFamily:"'DM Sans',sans-serif"}}>← Back to results</button>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:12}}>
            <Badge label={trial.phase} color={BRAND.skyLight} bg="rgba(255,255,255,0.15)"/>
            <Badge label={trial.statusLabel} color={trial.accepting?"#86efac":"rgba(255,255,255,0.5)"} bg="rgba(255,255,255,0.1)"/>
          </div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:"clamp(18px,3vw,30px)",color:BRAND.white,lineHeight:1.25,marginBottom:16}}>{trial.title}</h1>
          <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
            <a href={trial.sourceUrl} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:6,padding:"10px 20px",borderRadius:8,background:BRAND.white,color:BRAND.greenDark,fontWeight:700,fontSize:13,fontFamily:"'DM Sans',sans-serif"}}>
              <ExternalIcon/> View on ClinicalTrials.gov
            </a>
            <span style={{color:"rgba(255,255,255,0.45)",fontSize:12}}>{trial.id}</span>
          </div>
        </div>
      </div>
      <div style={{maxWidth:900,margin:"0 auto",padding:"36px clamp(16px,4vw,48px)"}}>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:26}}>
          {[["Condition",trial.condition],["Location",trial.location],["Age range",trial.ages],["Sponsor",trial.sponsor]].map(([k,v])=>(
            <div key={k} style={{background:BRAND.white,borderRadius:12,padding:"14px 18px",border:`1px solid rgba(74,124,89,0.12)`}}>
              <div style={{fontSize:10,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",color:BRAND.muted,marginBottom:4}}>{k}</div>
              <div style={{fontSize:14,fontWeight:600,color:BRAND.charcoal}}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{background:BRAND.white,borderRadius:16,padding:"26px 30px",border:`1px solid rgba(74,124,89,0.12)`,marginBottom:20}}>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:19,color:BRAND.charcoal,marginBottom:12}}>In plain language</h2>
          <p style={{fontSize:14,color:BRAND.muted,lineHeight:1.8}}>{trial.plainSummary}</p>
        </div>
        {trial.eligCriteria&&(
          <div style={{background:BRAND.white,borderRadius:16,padding:"26px 30px",border:`1px solid rgba(74,124,89,0.12)`,marginBottom:20}}>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:19,color:BRAND.charcoal,marginBottom:12}}>Eligibility criteria</h2>
            <p style={{fontSize:13,color:BRAND.muted,lineHeight:1.8,whiteSpace:"pre-line"}}>{trial.eligCriteria.slice(0,1400)}{trial.eligCriteria.length>1400?"…":""}</p>
            <a href={trial.sourceUrl} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:5,marginTop:14,fontSize:13,color:BRAND.green,fontWeight:600}}>Full criteria on ClinicalTrials.gov →</a>
          </div>
        )}
        <div style={{background:BRAND.skyLight+"60",borderRadius:12,padding:"16px 20px",border:`1px solid ${BRAND.sky}40`,fontSize:13,color:BRAND.muted,lineHeight:1.7}}>
          <strong style={{color:BRAND.charcoal}}>Always verify directly.</strong> Trial information changes. Use "View on ClinicalTrials.gov" above for the current, official eligibility criteria and study team contact details.
        </div>
      </div>
    </div>
  );
}

function ComparePage({comparison,setComparison,setPage,setDetailTrial}) {
  if (comparison.length===0) return (
    <div style={{paddingTop:90,textAlign:"center",padding:"160px 40px"}}>
      <div style={{fontSize:44,marginBottom:14}}>⚖️</div>
      <h2 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:22,color:BRAND.charcoal,marginBottom:10}}>No trials selected</h2>
      <p style={{color:BRAND.muted,marginBottom:24}}>Go to Find Trials and click "+ Compare" on any trials.</p>
      <button onClick={()=>setPage("search")} style={{padding:"12px 28px",borderRadius:10,background:BRAND.green,color:BRAND.white,fontWeight:600,fontFamily:"'DM Sans',sans-serif",fontSize:14}}>Browse Trials</button>
    </div>
  );
  const rows=[
    {label:"Phase",fn:t=>t.phase},
    {label:"Condition",fn:t=>t.condition},
    {label:"Status",fn:t=><Badge label={t.statusLabel} color={t.statusColor}/>},
    {label:"Recruiting",fn:t=><Badge label={t.accepting?"Yes — open":"Not currently"} color={t.accepting?BRAND.success:BRAND.muted}/>},
    {label:"Location",fn:t=>t.location},
    {label:"Ages",fn:t=>t.ages},
    {label:"Sponsor",fn:t=>t.sponsor},
    {label:"Source",fn:t=><a href={t.sourceUrl} target="_blank" rel="noopener noreferrer" style={{color:BRAND.green,fontWeight:600,fontSize:12,display:"inline-flex",alignItems:"center",gap:4,textDecoration:"underline"}}>ClinicalTrials.gov <ExternalIcon/></a>},
  ];
  const cW=Math.floor(76/comparison.length);
  return (
    <div style={{paddingTop:90}}>
      <div style={{background:BRAND.greenDark,padding:"46px clamp(16px,4vw,48px) 34px"}}>
        <div style={{maxWidth:1200,margin:"0 auto"}}>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:"clamp(22px,3vw,36px)",color:BRAND.white,marginBottom:8}}>Trial Comparison</h1>
          <p style={{color:"rgba(255,255,255,0.65)",fontSize:14}}>Comparing {comparison.length} trial{comparison.length>1?"s":""}. Every source link opens the official ClinicalTrials.gov record.</p>
        </div>
      </div>
      <div style={{maxWidth:1200,margin:"0 auto",padding:"34px clamp(16px,4vw,48px)",overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:520}}>
          <thead>
            <tr>
              <th style={{width:"24%",textAlign:"left",padding:"12px 14px",borderBottom:`2px solid ${BRAND.green}`,fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",color:BRAND.muted}}>Criteria</th>
              {comparison.map(t=>(
                <th key={t.id} style={{width:`${cW}%`,padding:"12px 14px",borderBottom:`2px solid ${BRAND.green}`,textAlign:"left"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                    <button onClick={()=>{setDetailTrial(t);setPage("detail");}} style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:12,color:BRAND.green,textAlign:"left",lineHeight:1.35,textDecoration:"underline"}}>{t.title.slice(0,55)}{t.title.length>55?"…":""}</button>
                    <button onClick={()=>setComparison(comparison.filter(c=>c.id!==t.id))} style={{fontSize:11,color:BRAND.danger,fontWeight:600,fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap"}}>Remove</button>
                  </div>
                  <div style={{fontSize:10,color:BRAND.muted,marginTop:4}}>{t.id}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row,ri)=>(
              <tr key={row.label} style={{background:ri%2===0?BRAND.white:BRAND.cream}}>
                <td style={{padding:"13px 14px",fontSize:13,fontWeight:600,color:BRAND.muted}}>{row.label}</td>
                {comparison.map(t=>(
                  <td key={t.id} style={{padding:"13px 14px",fontSize:13,color:BRAND.charcoal,borderLeft:`1px solid rgba(74,124,89,0.1)`}}>{row.fn(t)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProfilePage() {
  const [tab,setTab]         = useState("profile");
  const [profile,setProfile] = useState({label:"",relationship:"self",age:"",sex:"prefer-not",diagnosis:"",treatments:"",country:""});
  const [saved,setSaved]     = useState(false);

  // Auth state
  const [currentUser, setCurrentUser] = useState(()=>{
    try{ return JSON.parse(localStorage.getItem("phk_user")||"null"); }catch{ return null; }
  });
  const [showAuth,    setShowAuth]    = useState(false);
  const [authMode,    setAuthMode]    = useState("signin");
  const [authEmail,   setAuthEmail]   = useState(()=>localStorage.getItem("phk_email")||"");
  const [authPw,      setAuthPw]      = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError,   setAuthError]   = useState("");
  const [resetSent,   setResetSent]   = useState(false);

  // Saved trials
  const [savedTrials, setSavedTrials] = useState(()=>{
    try{ return JSON.parse(localStorage.getItem("phk_saved_trials")||"[]"); }catch{ return []; }
  });

  const inp = {width:"100%",padding:"11px 14px",borderRadius:8,border:`1.5px solid rgba(74,124,89,0.2)`,fontSize:14,fontFamily:"'DM Sans',sans-serif",color:BRAND.charcoal,background:BRAND.white,outline:"none"};

  const friendlyError = (code) => ({
    "auth/email-already-in-use":  "An account with this email already exists. Try signing in.",
    "auth/wrong-password":        "Incorrect password. Please try again.",
    "auth/invalid-credential":    "Incorrect email or password.",
    "auth/user-not-found":        "No account found with that email.",
    "auth/weak-password":         "Password must be at least 6 characters.",
    "auth/invalid-email":         "Please enter a valid email address.",
    "auth/too-many-requests":     "Too many attempts — please wait a moment.",
  }[code] || "Something went wrong. Please try again.");

  const handleAuth = async () => {
    if (!authEmail.trim() || !authPw.trim()) { setAuthError("Please enter both email and password."); return; }
    setAuthLoading(true); setAuthError("");
    try {
      if (!window.firebaseAuth) throw { code:"auth/not-configured" };
      const { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification } = window.firebaseAuth;
      let cred;
      if (authMode === "signup") {
        cred = await createUserWithEmailAndPassword(window.auth, authEmail.trim(), authPw);
        await sendEmailVerification(cred.user);
      } else {
        cred = await signInWithEmailAndPassword(window.auth, authEmail.trim(), authPw);
      }
      const user = { email: cred.user.email, uid: cred.user.uid };
      setCurrentUser(user);
      localStorage.setItem("phk_user", JSON.stringify(user));
      localStorage.setItem("phk_email", authEmail.trim());
      setShowAuth(false); setAuthPw(""); setAuthError("");
      // Load saved trials from Firestore
      if (window.db) {
        const { doc, getDoc } = window.firestore;
        const snap = await getDoc(doc(window.db, "users", user.uid));
        if (snap.exists() && snap.data().savedTrials) {
          const remote = snap.data().savedTrials;
          setSavedTrials(remote);
          localStorage.setItem("phk_saved_trials", JSON.stringify(remote));
        }
      }
    } catch(err) {
      setAuthError(err.code === "auth/not-configured"
        ? "Account system not yet connected — check back soon."
        : friendlyError(err.code));
    }
    setAuthLoading(false);
  };

  const handleSignOut = () => {
    if (window.firebaseAuth) window.firebaseAuth.signOut(window.auth).catch(()=>{});
    setCurrentUser(null);
    localStorage.removeItem("phk_user");
    setAuthPw(""); setAuthError("");
  };

  const handlePasswordReset = async () => {
    if (!authEmail.trim()) { setAuthError("Enter your email address above first."); return; }
    try {
      await window.firebaseAuth.sendPasswordResetEmail(window.auth, authEmail.trim());
      setResetSent(true); setAuthError("");
    } catch(err) { setAuthError(friendlyError(err.code)); }
  };

  const removeSavedTrial = async(id) => {
    const updated = savedTrials.filter(t=>t.id!==id);
    setSavedTrials(updated);
    localStorage.setItem("phk_saved_trials", JSON.stringify(updated));
    if (currentUser && window.db) {
      const { doc, updateDoc } = window.firestore;
      await updateDoc(doc(window.db,"users",currentUser.uid),{ savedTrials:updated }).catch(()=>{});
    }
  };

  return (
    <div style={{paddingTop:90}}>
      {/* Auth modal */}
      {showAuth && !currentUser && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:24}}>
          <div style={{background:BRAND.white,borderRadius:18,padding:"34px 38px",maxWidth:420,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
            <div style={{display:"flex",gap:0,marginBottom:22,borderRadius:9,overflow:"hidden",border:"1.5px solid rgba(74,124,89,0.2)"}}>
              {[["signin","Sign in"],["signup","Create account"]].map(([m,l])=>(
                <button key={m} onClick={()=>{setAuthMode(m);setAuthError("");setResetSent(false);}}
                  style={{flex:1,padding:"10px 0",fontSize:13,fontWeight:600,fontFamily:"'DM Sans',sans-serif",background:authMode===m?BRAND.greenDark:BRAND.white,color:authMode===m?BRAND.white:BRAND.muted,border:"none",cursor:"pointer"}}>
                  {l}
                </button>
              ))}
            </div>
            <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:7}}>Email address</label>
            <input type="email" value={authEmail} onChange={e=>{setAuthEmail(e.target.value);setAuthError("");}} onKeyDown={e=>e.key==="Enter"&&handleAuth()} placeholder="you@example.com"
              style={{width:"100%",padding:"11px 14px",borderRadius:9,border:`1.5px solid rgba(74,124,89,0.25)`,fontSize:14,fontFamily:"'DM Sans',sans-serif",color:BRAND.charcoal,outline:"none",marginBottom:12}}/>
            <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:7}}>Password</label>
            <input type="password" value={authPw} onChange={e=>{setAuthPw(e.target.value);setAuthError("");}} onKeyDown={e=>e.key==="Enter"&&handleAuth()} placeholder={authMode==="signup"?"At least 6 characters":"Your password"}
              style={{width:"100%",padding:"11px 14px",borderRadius:9,border:`1.5px solid rgba(74,124,89,0.25)`,fontSize:14,fontFamily:"'DM Sans',sans-serif",color:BRAND.charcoal,outline:"none",marginBottom:6}}/>
            {authError && <p style={{fontSize:12,color:BRAND.danger,fontWeight:500,margin:"6px 0 10px"}}>{authError}</p>}
            {resetSent && <p style={{fontSize:12,color:BRAND.success,fontWeight:500,margin:"6px 0 10px"}}>✓ Reset email sent — check your inbox.</p>}
            {authMode==="signup"&&<p style={{fontSize:11,color:BRAND.muted,lineHeight:1.6,margin:"6px 0 14px"}}>Creating an account lets you save trials permanently across devices. We'll send a verification email.</p>}
            <div style={{display:"flex",gap:10,marginTop:16}}>
              <button onClick={handleAuth} disabled={authLoading}
                style={{flex:1,padding:"12px 0",borderRadius:9,background:BRAND.greenDark,color:BRAND.white,fontWeight:600,fontSize:14,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:authLoading?"default":"pointer",opacity:authLoading?0.6:1}}>
                {authLoading?"Please wait…":authMode==="signup"?"Create account":"Sign in"}
              </button>
              <button onClick={()=>{setShowAuth(false);setAuthPw("");setAuthError("");}}
                style={{padding:"12px 18px",borderRadius:9,background:BRAND.cream,color:BRAND.muted,fontWeight:600,fontSize:14,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:"pointer"}}>
                Cancel
              </button>
            </div>
            {authMode==="signin"&&(
              <button onClick={handlePasswordReset}
                style={{width:"100%",marginTop:14,fontSize:12,color:BRAND.muted,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",background:"transparent",border:"none",textDecoration:"underline"}}>
                Forgot password? Send reset email
              </button>
            )}
          </div>
        </div>
      )}

      <div style={{background:BRAND.greenDark,padding:"46px clamp(16px,4vw,48px) 0"}}>
        <div style={{maxWidth:900,margin:"0 auto"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12,marginBottom:22}}>
            <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:"clamp(22px,3vw,34px)",color:BRAND.white}}>My Profile</h1>
            {currentUser ? (
              <div style={{display:"flex",alignItems:"center",gap:10,background:"rgba(255,255,255,0.12)",borderRadius:10,padding:"8px 14px"}}>
                <div style={{width:28,height:28,borderRadius:"50%",background:BRAND.green,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:BRAND.white}}>
                  {currentUser.email.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.6)"}}>Signed in as</div>
                  <div style={{fontSize:13,color:BRAND.white,fontWeight:600,maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{currentUser.email}</div>
                </div>
                <button onClick={handleSignOut} style={{fontSize:11,color:"rgba(255,255,255,0.6)",fontWeight:600,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",background:"transparent",border:"none",textDecoration:"underline"}}>Sign out</button>
              </div>
            ) : (
              <button onClick={()=>{setShowAuth(true);setAuthMode("signin");setAuthError("");}}
                style={{padding:"9px 18px",borderRadius:9,background:"rgba(255,255,255,0.15)",border:"1.5px solid rgba(255,255,255,0.3)",color:BRAND.white,fontSize:13,fontWeight:600,fontFamily:"'DM Sans',sans-serif",cursor:"pointer"}}>
                Sign in / Create account
              </button>
            )}
          </div>
          <div style={{display:"flex",gap:4}}>
            {[["profile","My Profile"],["saved","Saved Trials"]].map(([id,label])=>(
              <button key={id} onClick={()=>setTab(id)} style={{padding:"11px 20px",borderRadius:"8px 8px 0 0",fontFamily:"'DM Sans',sans-serif",fontWeight:600,fontSize:14,background:tab===id?BRAND.cream:"rgba(255,255,255,0.1)",color:tab===id?BRAND.greenDark:"rgba(255,255,255,0.7)"}}>
                {label}
                {id==="saved"&&savedTrials.length>0&&<span style={{marginLeft:6,background:BRAND.green,color:BRAND.white,borderRadius:"50%",width:17,height:17,fontSize:10,fontWeight:700,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{savedTrials.length}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{maxWidth:900,margin:"0 auto",padding:"36px clamp(16px,4vw,48px)"}}>
        {tab==="profile"&&(
          <div className="fade-in">
            <div style={{background:BRAND.white,borderRadius:16,padding:"30px 34px",border:`1px solid rgba(74,124,89,0.12)`,marginBottom:20}}>
              <h2 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:19,color:BRAND.charcoal,marginBottom:6}}>Patient profile</h2>
              <p style={{fontSize:13,color:BRAND.muted,marginBottom:24}}>Create a profile for yourself or someone you care for. {currentUser?"Your profile is linked to your account.":"Sign in to save permanently across devices."}</p>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                {[["Profile label","label","e.g. Mom (Margaret), Myself"],["Age","age","e.g. 67"],["Primary diagnosis","diagnosis","e.g. Parkinson's disease"],["Country","country","e.g. United States"]].map(([label,key,ph])=>(
                  <div key={key}>
                    <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:6}}>{label}</label>
                    <input value={profile[key]} onChange={e=>setProfile({...profile,[key]:e.target.value})} placeholder={ph} style={inp} onFocus={e=>e.target.style.borderColor=BRAND.green} onBlur={e=>e.target.style.borderColor="rgba(74,124,89,0.2)"}/>
                  </div>
                ))}
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:6}}>Relationship</label>
                  <select value={profile.relationship} onChange={e=>setProfile({...profile,relationship:e.target.value})} style={inp}>
                    {["self","parent","spouse","child","sibling","other"].map(r=><option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:6}}>Sex</label>
                  <select value={profile.sex} onChange={e=>setProfile({...profile,sex:e.target.value})} style={inp}>
                    {[["prefer-not","Prefer not to say"],["male","Male"],["female","Female"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div style={{gridColumn:"1/-1"}}>
                  <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:6}}>Prior treatments</label>
                  <textarea value={profile.treatments} onChange={e=>setProfile({...profile,treatments:e.target.value})} placeholder={"e.g. Donepezil (ongoing)\nChemotherapy – completed 2023"} rows={3} style={{...inp,resize:"vertical"}} onFocus={e=>e.target.style.borderColor=BRAND.green} onBlur={e=>e.target.style.borderColor="rgba(74,124,89,0.2)"}/>
                </div>
              </div>
              <div style={{display:"flex",gap:12,marginTop:22,alignItems:"center"}}>
                <button onClick={()=>setSaved(true)} style={{padding:"11px 26px",borderRadius:8,background:BRAND.green,color:BRAND.white,fontWeight:600,fontSize:14,fontFamily:"'DM Sans',sans-serif"}}>Save Profile</button>
                {saved&&<span style={{fontSize:13,color:BRAND.success,fontWeight:600}}>✓ Saved</span>}
              </div>
            </div>
            {!currentUser ? (
              <div style={{background:BRAND.skyLight+"50",borderRadius:12,padding:"18px 22px",border:`1px solid ${BRAND.sky}40`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,flexWrap:"wrap"}}>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:BRAND.charcoal,marginBottom:4}}>Save trials and profiles permanently</div>
                  <div style={{fontSize:13,color:BRAND.muted,lineHeight:1.6}}>Create a free account to access your data from any device and get notified when trials open.</div>
                </div>
                <button onClick={()=>{setShowAuth(true);setAuthMode("signup");setAuthError("");}}
                  style={{padding:"10px 22px",borderRadius:9,background:BRAND.greenDark,color:BRAND.white,fontWeight:600,fontSize:13,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:"pointer",flexShrink:0}}>
                  Create free account
                </button>
              </div>
            ) : (
              <div style={{background:GREEN_L,borderRadius:12,padding:"14px 20px",border:"1px solid #5DCAA5",fontSize:13,color:"#085041",display:"flex",alignItems:"center",gap:8}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                Signed in as <strong>{currentUser.email}</strong> — your saved trials sync to your account.
              </div>
            )}
          </div>
        )}

        {tab==="saved"&&(
          <div className="fade-in">
            {!currentUser&&(
              <div style={{background:BRAND.cream,borderRadius:14,padding:"28px 32px",marginBottom:20,textAlign:"center",border:`1px solid rgba(74,124,89,0.15)`}}>
                <div style={{fontSize:32,marginBottom:10}}>🔐</div>
                <h3 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:17,color:BRAND.charcoal,marginBottom:8}}>Sign in to save trials</h3>
                <p style={{fontSize:14,color:BRAND.muted,marginBottom:18,lineHeight:1.7}}>Create a free account to bookmark trials and access them from any device.</p>
                <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
                  <button onClick={()=>{setShowAuth(true);setAuthMode("signup");setAuthError("");}}
                    style={{padding:"11px 24px",borderRadius:9,background:BRAND.greenDark,color:BRAND.white,fontWeight:600,fontSize:14,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:"pointer"}}>Create free account</button>
                  <button onClick={()=>{setShowAuth(true);setAuthMode("signin");setAuthError("");}}
                    style={{padding:"11px 24px",borderRadius:9,background:BRAND.white,color:BRAND.green,fontWeight:600,fontSize:14,fontFamily:"'DM Sans',sans-serif",border:`1.5px solid ${BRAND.green}`,cursor:"pointer"}}>Sign in</button>
                </div>
              </div>
            )}
            {savedTrials.length===0 ? (
              <div style={{textAlign:"center",padding:"60px 20px",color:BRAND.muted}}>
                <div style={{width:56,height:56,borderRadius:"50%",background:BRAND.green+"15",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BRAND.green} strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z"/></svg>
                </div>
                <h3 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:17,color:BRAND.charcoal,marginBottom:8}}>No saved trials yet</h3>
                <p style={{fontSize:14}}>Browse trials and click the bookmark icon to save them here.</p>
              </div>
            ) : (
              <div>
                <p style={{fontSize:13,color:BRAND.muted,marginBottom:16}}>{savedTrials.length} saved trial{savedTrials.length!==1?"s":""}</p>
                {savedTrials.map(trial=>(
                  <div key={trial.id} style={{background:BRAND.white,borderRadius:14,border:"0.5px solid rgba(74,124,89,0.14)",padding:"18px 20px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:11,color:BRAND.muted,marginBottom:4,fontFamily:"monospace"}}>{trial.id}</div>
                      <div style={{fontSize:14,fontWeight:600,color:BRAND.charcoal,marginBottom:6,lineHeight:1.4}}>{trial.title}</div>
                      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                        {trial.status&&<span style={{fontSize:11,background:BRAND.green+"15",color:BRAND.green,padding:"2px 8px",borderRadius:20,fontWeight:600}}>{trial.status}</span>}
                        {trial.phase&&<span style={{fontSize:11,background:BRAND.cream,color:BRAND.muted,padding:"2px 8px",borderRadius:20}}>{trial.phase}</span>}
                        {trial.sponsor&&<span style={{fontSize:11,background:BRAND.cream,color:BRAND.muted,padding:"2px 8px",borderRadius:20}}>{trial.sponsor.length>30?trial.sponsor.slice(0,30)+"…":trial.sponsor}</span>}
                      </div>
                    </div>
                    <button onClick={()=>removeSavedTrial(trial.id)} title="Remove from saved"
                      style={{width:30,height:30,borderRadius:8,background:"#fee2e2",border:`1px solid ${BRAND.danger}30`,color:BRAND.danger,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AboutPage() {
  // ── Resource links — edit these freely ──────────────────────
  const SOCIAL_LINKS = [
    {
      label: "LinkedIn",
      description: "Connect with the founder",
      url: "https://www.linkedin.com/in/nicholasvogler/",
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/>
          <circle cx="4" cy="4" r="2"/>
        </svg>
      ),
      bg: "#0077b5", hoverBg: "#005885", textColor: "#ffffff",
    },
    {
      label: "Instagram",
      description: "Follow us on Instagram",
      url: "https://www.instagram.com/pangaeas.hk",
      // ↑ Replace with your actual Instagram handle URL
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
          <circle cx="12" cy="12" r="4"/>
          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
        </svg>
      ),
      bg: "#C13584", hoverBg: "#a02d6e", textColor: "#ffffff",
    },
  ];

  const RESOURCE_LINKS = [
    {
      label: "NAMI",
      fullName: "National Alliance on Mental Illness",
      description: "Support, education, and advocacy for people affected by mental illness.",
      url: "https://www.nami.org",
      tag: "Mental health",
    },
    {
      label: "ClinicalTrials.gov",
      fullName: "U.S. National Library of Medicine",
      description: "The official U.S. registry of all federally and privately funded clinical studies.",
      url: "https://clinicaltrials.gov",
      tag: "Trial registry",
    },
    {
      label: "WHO ICTRP",
      fullName: "World Health Organization",
      description: "The WHO's international clinical trials registry platform — covers trials worldwide.",
      url: "https://www.who.int/clinical-trials-registry-platform",
      tag: "Trial registry",
    },
    {
      label: "NIH MedlinePlus",
      fullName: "U.S. National Institutes of Health",
      description: "Trusted health information for patients and families in plain language.",
      url: "https://medlineplus.gov",
      tag: "Health information",
    },
    {
      label: "Patient Advocate Foundation",
      fullName: "Patient Advocate Foundation",
      description: "Helps patients access care and navigate insurance, financial, and legal issues.",
      url: "https://www.patientadvocate.org",
      tag: "Patient support",
    },
    {
      label: "CMNH",
      fullName: "Children's Miracle Network Hospitals",
      description: "Raises funds for 170+ children's hospitals across North America, supporting pediatric research, treatment, and care.",
      url: "https://www.cmnhospitals.org",
      tag: "Pediatric support",
    },
    // ── Add more resources here in the same format ────────────
    // {
    //   label: "Short name",
    //   fullName: "Full organisation name",
    //   description: "One sentence about what they do.",
    //   url: "https://...",
    //   tag: "Category",
    // },
  ];

  return (
    <div style={{ paddingTop: 90 }}>
      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${BRAND.greenDark} 0%, ${BRAND.green} 100%)`, padding: "80px clamp(16px,4vw,80px)", textAlign: "center" }}>
        <div className="fade-up" style={{ marginBottom: 20 }}><Logo size={56} textColor={BRAND.white} /></div>
        <h1 className="fade-up delay-1" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "clamp(28px,5vw,52px)", color: BRAND.white, lineHeight: 1.15, maxWidth: 680, margin: "0 auto 20px" }}>
          Why we built this
        </h1>
        <p className="fade-up delay-2" style={{ fontSize: "clamp(15px,2vw,17px)", color: "rgba(255,255,255,0.84)", maxWidth: 580, margin: "0 auto", lineHeight: 1.8 }}>
          Clinical trials are some of the most promising frontiers of medicine. But finding them — understanding them — knowing if you qualify — has never been easy. We built Pangaea's Health and Knowledge to change that.
        </p>
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "72px clamp(16px,4vw,48px)" }}>

        {/* Mission cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 60 }}>
          {[
            { title: "Our name", body: "Pangaea was the ancient supercontinent — every landmass connected as one. We chose it because illness doesn't respect borders, and research shouldn't either. Health and Knowledge captures our two core beliefs: that every person deserves access to the best medical options, and that understanding those options is a right, not a privilege." },
            { title: "Our mission", body: "To build the most honest, accessible, and patient-centred clinical trial platform on earth. We translate clinical prose into plain language, check eligibility rules without false hope, and keep users informed as the science evolves — week by week, trial by trial." },
            { title: "Why rule-based matching", body: "We deliberately chose not to use AI probability scores for matching. Clinical trials have hard eligibility gates — one failed criterion means you're out. Showing someone a '78% match' when they fail a hard cutoff is misleading. We show you exactly which rules pass, fail, or need more information." },
            { title: "Hope without false hope", body: "Even when no trial is open today, a Phase 1 trial today may become a Phase 3 trial that changes everything. Follow any trial at any stage. When it opens, or when the science advances, we will notify you. No trial today never means no hope." },
          ].map(s => (
            <div key={s.title} style={{ background: BRAND.white, borderRadius: 16, padding: "26px 30px", border: `1px solid rgba(74,124,89,0.12)` }}>
              <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 17, color: BRAND.green, marginBottom: 10 }}>{s.title}</h3>
              <p style={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.8 }}>{s.body}</p>
            </div>
          ))}
        </div>

        {/* ── Founders section ─────────────────────────────────── */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: BRAND.green, marginBottom: 10 }}>The team</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "clamp(22px,3vw,34px)", color: BRAND.charcoal }}>Founders & Members</h2>
        </div>

        <div style={{ background: BRAND.white, borderRadius: 20, padding: "38px 42px", border: `1px solid rgba(74,124,89,0.15)`, textAlign: "center", maxWidth: 640, margin: "0 auto 28px" }}>
          {/*
            TO ADD YOUR PHOTO:
            1. Put your image file in the public/ folder (e.g. public/founder.jpg)
            2. Delete the green circle div below
            3. Paste this in its place:
            <img
              src="/founder.jpg"
              alt="Founder of Pangaea's HK"
              style={{ width:120, height:120, borderRadius:"50%", objectFit:"cover",
                       margin:"0 auto 18px", display:"block", border:"3px solid #4a7c59" }}
            />
          */}
          <div style={{ width: 90, height: 90, borderRadius: "50%", background: BRAND.green, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
            <Logo size={54} withText={false} />
          </div>

          {/* ── REPLACE: your name ── */}
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: 21, color: BRAND.charcoal, marginBottom: 4 }}>
            Nicholas Vogler
          </h3>

          {/* ── REPLACE: your title ── */}
          <p style={{ fontSize: 12, color: BRAND.green, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 18 }}>
            Founder & Director
          </p>

          {/* ── REPLACE: your bio ── */}
          <p style={{ fontSize: 14, color: BRAND.muted, lineHeight: 1.85, marginBottom: 22 }}>
            This platform was born from the belief that when someone you love is diagnosed with a serious illness,
they deserve every possible option — clearly presented, honestly assessed, and easily found.
I am currently studying biomedical engineering with a focus in tissue engineering at Johns Hopkins University,
with a background in biochemistry and clinical research, including work supporting clinical trials at Stanford and Harvard.
Despite this experience, I became a patient myself at 26 when I was diagnosed with an unresolved clotting disorder.

What followed was both frightening and eye-opening. I experienced more than 20 blood clots, including severe complications
in my lungs — an especially sobering reality given my background in lung research. I was enrolled in a clinical trial,
yet even with my experience, I did not fully understand what I was participating in. When the treatment failed and my condition worsened,
I found myself overwhelmed, uncertain, and searching for answers that were difficult to find and even harder to understand.

Through my background in medical bioethics and clinical research, I began to recognize a critical gap:
there is a significant lack of accessible, understandable information about clinical trials that could potentially help patients.
Too often, individuals are only presented with a limited set of options, without a clear understanding of what else may exist.

Pangaea's Health and Knowledge was created to change that — to provide a comprehensive and accessible breakdown of clinical trials,
not just the ones immediately presented, but a broader view of what is available, translated into language that is easier to understand.
My hope is that this platform can provide clarity, empower decision-making, and offer a sense of direction to those facing
currently incurable conditions, those who have not found success in previous trials, and the teams working to care for them.
          </p>

          {/* ── Social links ── */}
          {SOCIAL_LINKS.length > 0 && (
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: 18 }}>
              {SOCIAL_LINKS.map(link => (
                <a
                  key={link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 7,
                    padding: "9px 18px", borderRadius: 9,
                    background: link.bg, color: link.textColor,
                    fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: 13,
                    transition: "opacity 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
                  onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                >
                  {link.icon}
                  {link.label}
                </a>
              ))}
            </div>
          )}

          <div style={{ padding: "14px 18px", background: BRAND.cream, borderRadius: 10, fontSize: 13, color: BRAND.muted }}>
            Want to join the team or contribute?{" "}
            <a href="mailto:hello@pangaeashk.org" style={{ color: BRAND.green, fontWeight: 600 }}>
              hello@pangaeashk.org
            </a>
          </div>
        </div>

        {/* ── Contributors ─────────────────────────────────────── */}
        <div style={{ background: BRAND.white, borderRadius: 20, padding: "38px 42px", border: `1px solid rgba(74,124,89,0.15)`, textAlign: "center", maxWidth: 640, margin: "0 auto 28px" }}>
          {/*
            TO ADD JODY'S PHOTO:
            1. Put the image file in the public/ folder (e.g. public/jody.jpg)
            2. Delete the green circle div below
            3. Paste this in its place:
            <img
              src="/jody.jpg"
              alt="Jody"
              style={{ width:120, height:120, borderRadius:"50%", objectFit:"cover",
                       margin:"0 auto 18px", display:"block", border:"3px solid #4a7c59" }}
            />
          */}
          <div style={{ width: 90, height: 90, borderRadius: "50%", background: BRAND.sky, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
            <svg width="54" height="54" viewBox="0 0 80 80" fill="none">
              <circle cx="40" cy="28" r="16" fill="rgba(255,255,255,0.9)"/>
              <path d="M14 68 Q16 48 40 48 Q64 48 66 68Z" fill="rgba(255,255,255,0.9)"/>
            </svg>
          </div>

          {/* ── Contributors ── */}
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: 21, color: BRAND.charcoal, marginBottom: 4 }}>
            Contributors, coming soon!
          </h3>

          {/* ── Change to COntributors title / role ── */}
          <p style={{ fontSize: 12, color: BRAND.green, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 18 }}>
            Contributor
          </p>

          {/* ── Replace this with Contributors bio ── */}
          <p style={{ fontSize: 14, color: BRAND.muted, lineHeight: 1.85, marginBottom: 22 }}>
            Coming Soon
          </p>

          {/* ── Add Contributors social links here if she wants them ── */}
          {/* Example — uncomment and fill in to add a LinkedIn button:
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginBottom: 18 }}>
            <a href="https://www.linkedin.com/in/Contributor" target="_blank" rel="noopener noreferrer"
              style={{ display:"inline-flex", alignItems:"center", gap:7, padding:"9px 18px", borderRadius:9, background:"#0077b5", color:"#fff", fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:13 }}>
              LinkedIn
            </a>
          </div>
          */}

          <div style={{ padding: "14px 18px", background: BRAND.cream, borderRadius: 10, fontSize: 13, color: BRAND.muted }}>
            Questions or collaboration?{" "}
            <a href="mailto:hello@pangaeashk.org" style={{ color: BRAND.green, fontWeight: 600 }}>
              hello@pangaeashk.org
            </a>
          </div>
        </div>

        {/* ── Values ─────────────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 64 }}>
          {[
            ["🌍", "Global reach", "We pull from every major registry — not just the US."],
            ["🔍", "Radical transparency", "Every eligibility rule is shown. No black boxes."],
            ["💚", "Patient first", "Every decision is made by asking: would this help a scared caregiver at 2am?"],
          ].map(([icon, title, body]) => (
            <div key={title} style={{ background: BRAND.cream, borderRadius: 14, padding: "22px 18px", textAlign: "center" }}>
              <div style={{ fontSize: 26, marginBottom: 10 }}>{icon}</div>
              <h4 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 14, color: BRAND.charcoal, marginBottom: 7 }}>{title}</h4>
              <p style={{ fontSize: 12, color: BRAND.muted, lineHeight: 1.65 }}>{body}</p>
            </div>
          ))}
        </div>

        {/* ── Resource links ──────────────────────────────────── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: BRAND.green, marginBottom: 10 }}>
            Additional resources
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "clamp(20px,3vw,30px)", color: BRAND.charcoal, marginBottom: 6 }}>
            Trusted organisations
          </h2>
          <p style={{ fontSize: 14, color: BRAND.muted, marginBottom: 28, lineHeight: 1.7 }}>
            Pangaea's HK supports these organisations — with each one of them being a trusted source of support, provide information, or advocate for patients and families.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
            {RESOURCE_LINKS.map(r => (
              <a
                key={r.label}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "block", background: BRAND.white, borderRadius: 14,
                  padding: "20px 22px", border: `1px solid rgba(74,124,89,0.14)`,
                  textDecoration: "none", transition: "transform 0.18s, box-shadow 0.18s",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.09)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <span style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 15, color: BRAND.charcoal }}>{r.label}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", padding: "2px 8px", borderRadius: 20, background: BRAND.green + "18", color: BRAND.green }}>
                    {r.tag}
                  </span>
                </div>
                <p style={{ fontSize: 11, color: BRAND.muted, marginBottom: 6, fontStyle: "italic" }}>{r.fullName}</p>
                <p style={{ fontSize: 13, color: BRAND.charcoal, lineHeight: 1.6 }}>{r.description}</p>
                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: BRAND.green, fontWeight: 600 }}>
                  Visit site
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* ── Contact Us ──────────────────────────────────────── */}
        <div style={{ background: `linear-gradient(135deg, ${BRAND.greenDark} 0%, ${BRAND.green} 100%)`, borderRadius: 20, padding: "48px 40px", textAlign: "center", marginTop: 20 }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "clamp(20px,3vw,30px)", color: BRAND.white, marginBottom: 10 }}>
            Get in touch
          </h2>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.75)", lineHeight: 1.8, maxWidth: 480, margin: "0 auto 28px" }}>
            Whether you're a patient, caregiver, researcher, or institution — we want to hear from you. Questions, partnership enquiries, feedback, or just to say hello.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="mailto:hello@pangaeashk.org"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 26px", borderRadius: 10, background: BRAND.white, color: BRAND.greenDark, fontWeight: 700, fontSize: 14, fontFamily: "'DM Sans', sans-serif", textDecoration: "none", transition: "opacity 0.15s" }}
              onMouseEnter={e=>e.currentTarget.style.opacity="0.88"}
              onMouseLeave={e=>e.currentTarget.style.opacity="1"}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
              hello@pangaeashk.org
            </a>
            <a href="https://www.instagram.com/pangaeas.hk" target="_blank" rel="noopener noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 26px", borderRadius: 10, background: "rgba(255,255,255,0.15)", color: BRAND.white, fontWeight: 600, fontSize: 14, fontFamily: "'DM Sans', sans-serif", textDecoration: "none", border: "1.5px solid rgba(255,255,255,0.3)", transition: "background 0.15s" }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.22)"}
              onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.15)"}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
              </svg>
              @pangaeas.hk
            </a>
          </div>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", marginTop: 20 }}>
            We read every message and aim to respond within 48 hours.
          </p>
        </div>

      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// COMMUNITY BOARD — with admin moderation queue
// Admin password — change this to your own private word:
const ADMIN_PASSWORD = "phk-admin-2026";
// ─────────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  suggestion:{ label:"💡 Suggestion", bg:"#e6f1fb", color:"#185fa5" },
  review:    { label:"⭐ Review",     bg:"#e1f5ee", color:"#0f6e56" },
  bug:       { label:"🐛 Bug report", bg:"#faece7", color:"#993c1d" },
  other:     { label:"💬 Other",      bg:"#f1efe8", color:"#5f5e5a" },
};

function timeAgo(ts) {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff/60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins/60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs/24)}d ago`;
}

function TypeBadge({ type }) {
  const c = TYPE_CONFIG[type] || TYPE_CONFIG.other;
  return <span style={{display:"inline-flex",alignItems:"center",padding:"2px 9px",borderRadius:20,fontSize:11,fontWeight:600,letterSpacing:"0.04em",textTransform:"uppercase",background:c.bg,color:c.color}}>{c.label}</span>;
}

function AdminUnlockModal({ onClose, onUnlock }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);
  const tryUnlock = () => {
    if (pw === ADMIN_PASSWORD) { onUnlock(); onClose(); }
    else { setErr(true); setPw(""); }
  };
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:200,padding:24}}>
      <div style={{background:BRAND.white,borderRadius:18,padding:"34px 38px",maxWidth:400,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{width:48,height:48,borderRadius:"50%",background:BRAND.green+"18",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={BRAND.green} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        </div>
        <h3 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:20,color:BRAND.charcoal,marginBottom:6,textAlign:"center"}}>Admin access</h3>
        <p style={{fontSize:13,color:BRAND.muted,marginBottom:22,lineHeight:1.65,textAlign:"center"}}>Enter your admin password to review and approve pending posts.</p>
        <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:7}}>Admin password</label>
        <input type="password" value={pw} onChange={e=>{setPw(e.target.value);setErr(false);}} onKeyDown={e=>e.key==="Enter"&&tryUnlock()} autoFocus placeholder="Enter your admin password…"
          style={{width:"100%",padding:"12px 14px",borderRadius:9,border:`1.5px solid ${err?BRAND.danger:"rgba(74,124,89,0.25)"}`,fontSize:14,fontFamily:"'DM Sans',sans-serif",color:BRAND.charcoal,background:err?"#fff5f5":BRAND.white,outline:"none",marginBottom:6}}/>
        {err && <p style={{fontSize:12,color:BRAND.danger,fontWeight:500,marginBottom:12}}>Incorrect password. Try again.</p>}
        <div style={{display:"flex",gap:10,marginTop:18}}>
          <button onClick={tryUnlock} style={{flex:1,padding:"12px 0",borderRadius:9,background:BRAND.greenDark,color:BRAND.white,fontWeight:600,fontSize:14,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:"pointer"}}>Unlock</button>
          <button onClick={onClose} style={{flex:1,padding:"12px 0",borderRadius:9,background:BRAND.cream,color:BRAND.muted,fontWeight:600,fontSize:14,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:"pointer"}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function PendingPostCard({ post, onApprove, onReject }) {
  const [acting, setActing] = useState(false);
  const handle = async(fn) => { setActing(true); await fn(); setActing(false); };
  return (
    <div style={{background:BRAND.white,borderRadius:14,border:`1.5px solid ${BRAND.amber}50`,padding:"18px 20px",marginBottom:12,position:"relative"}}>
      <div style={{position:"absolute",top:14,right:14}}>
        <span style={{fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",padding:"3px 9px",borderRadius:20,background:"#faeeda",color:BRAND.amber}}>Pending review</span>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,paddingRight:120}}>
        <div style={{width:34,height:34,borderRadius:"50%",background:BRAND.amber+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:700,color:BRAND.amber,flexShrink:0}}>
          {(post.name||"A").charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{fontSize:14,fontWeight:600,color:BRAND.charcoal}}>{post.name||"Anonymous"}</div>
          <div style={{fontSize:11,color:BRAND.muted}}>{timeAgo(post.ts)}</div>
        </div>
        <TypeBadge type={post.type}/>
      </div>
      <p style={{fontSize:14,color:BRAND.charcoal,lineHeight:1.75,marginBottom:14}}>{post.body}</p>
      <div style={{display:"flex",gap:9}}>
        <button onClick={()=>handle(onApprove)} disabled={acting}
          style={{flex:1,padding:"10px 0",borderRadius:8,background:BRAND.success,color:BRAND.white,fontWeight:600,fontSize:13,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:acting?"default":"pointer",opacity:acting?0.6:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          Approve — publish to board
        </button>
        <button onClick={()=>handle(onReject)} disabled={acting}
          style={{padding:"10px 18px",borderRadius:8,background:"#fee2e2",color:BRAND.danger,fontWeight:600,fontSize:13,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:acting?"default":"pointer",opacity:acting?0.6:1,display:"flex",alignItems:"center",gap:6}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          Reject
        </button>
      </div>
    </div>
  );
}

function ApprovedPostCard({ post, onLike, liked, isAdmin, onDelete, onReply }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showReplyBox, setShowReplyBox]   = useState(false);
  const [replyText,    setReplyText]      = useState(post.adminReply || "");
  const [savingReply,  setSavingReply]    = useState(false);

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setSavingReply(true);
    await onReply(post.id, replyText.trim());
    setSavingReply(false);
    setShowReplyBox(false);
  };

  const deleteReply = async () => {
    await onReply(post.id, "");
    setReplyText("");
  };

  return (
    <div style={{background:BRAND.white,borderRadius:14,border:"0.5px solid rgba(74,124,89,0.14)",padding:"20px 22px",marginBottom:14,transition:"box-shadow 0.2s",position:"relative"}}
      onMouseEnter={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.08)"}
      onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>

      {/* Admin action buttons — top right */}
      {isAdmin && !confirmDelete && (
        <div style={{position:"absolute",top:14,right:14,display:"flex",gap:6}}>
          {/* Reply button */}
          <button onClick={()=>{ setShowReplyBox(v=>!v); setReplyText(post.adminReply||""); }}
            title={post.adminReply ? "Edit reply" : "Reply to this post"}
            style={{width:28,height:28,borderRadius:7,background:post.adminReply?"#e1f5ee":"rgba(74,124,89,0.1)",border:`1px solid ${post.adminReply?BRAND.success+"60":"rgba(74,124,89,0.25)"}`,color:post.adminReply?BRAND.success:BRAND.green,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
            </svg>
          </button>
          {/* Delete button */}
          <button onClick={()=>setConfirmDelete(true)} title="Delete this post"
            style={{width:28,height:28,borderRadius:7,background:"#fee2e2",border:`1px solid ${BRAND.danger}30`,color:BRAND.danger,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
            </svg>
          </button>
        </div>
      )}

      {/* Delete confirmation */}
      {isAdmin && confirmDelete && (
        <div style={{position:"absolute",top:10,right:10,background:BRAND.white,border:`1.5px solid ${BRAND.danger}`,borderRadius:10,padding:"12px 14px",boxShadow:"0 4px 16px rgba(0,0,0,0.12)",zIndex:10,minWidth:210}}>
          <p style={{fontSize:12,fontWeight:600,color:BRAND.danger,marginBottom:10}}>Delete this post permanently?</p>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>{onDelete(post.id);setConfirmDelete(false);}} style={{flex:1,padding:"7px 0",borderRadius:7,background:BRAND.danger,color:BRAND.white,fontWeight:700,fontSize:12,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:"pointer"}}>Yes, delete</button>
            <button onClick={()=>setConfirmDelete(false)} style={{flex:1,padding:"7px 0",borderRadius:7,background:BRAND.cream,color:BRAND.muted,fontWeight:600,fontSize:12,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}

      {/* Post header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:36,height:36,borderRadius:"50%",background:BRAND.green+"20",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,fontWeight:700,color:BRAND.greenDark,flexShrink:0}}>
            {(post.name||"A").charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{fontSize:14,fontWeight:600,color:BRAND.charcoal}}>{post.name||"Anonymous"}</div>
            <div style={{fontSize:11,color:BRAND.muted,marginTop:2}}>{timeAgo(post.ts)}</div>
          </div>
        </div>
        <div style={{paddingRight:isAdmin?68:0}}><TypeBadge type={post.type}/></div>
      </div>

      {/* Post body */}
      <p style={{fontSize:14,color:BRAND.charcoal,lineHeight:1.75,marginBottom:14}}>{post.body}</p>

      {/* Existing admin reply (visible to everyone) */}
      {post.adminReply && !showReplyBox && (
        <div style={{background:"#e1f5ee",borderRadius:10,padding:"12px 16px",marginBottom:14,borderLeft:`3px solid ${BRAND.green}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <div style={{display:"flex",alignItems:"center",gap:7}}>
              <div style={{width:22,height:22,borderRadius:"50%",background:BRAND.greenDark,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <svg width="11" height="11" viewBox="0 0 80 80" fill="none">
                  <circle cx="40" cy="32" r="22" fill="#87c5d6"/>
                  <path d="M22 20 Q28 14 38 18 Q44 22 42 30 Q38 36 32 34 Q24 30 22 20Z" fill="#fff"/>
                  <path d="M46 18 Q56 16 58 26 Q58 34 52 38 Q46 40 44 34 Q42 26 46 18Z" fill="#fff"/>
                </svg>
              </div>
              <span style={{fontSize:12,fontWeight:700,color:BRAND.greenDark}}>Pangaea's HK</span>
              <span style={{fontSize:11,color:BRAND.muted}}>· Team response</span>
            </div>
            {isAdmin && (
              <div style={{display:"flex",gap:6}}>
                <button onClick={()=>{ setShowReplyBox(true); setReplyText(post.adminReply); }}
                  style={{fontSize:11,color:BRAND.green,fontWeight:600,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",background:"transparent",border:"none"}}>Edit</button>
                <span style={{color:BRAND.muted,fontSize:11}}>·</span>
                <button onClick={deleteReply}
                  style={{fontSize:11,color:BRAND.danger,fontWeight:600,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",background:"transparent",border:"none"}}>Remove</button>
              </div>
            )}
          </div>
          <p style={{fontSize:13,color:"#085041",lineHeight:1.7}}>{post.adminReply}</p>
        </div>
      )}

      {/* Admin reply compose box */}
      {isAdmin && showReplyBox && (
        <div style={{background:"#e1f5ee",borderRadius:10,padding:"14px 16px",marginBottom:14,border:`1px solid ${BRAND.green}30`}}>
          <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:10}}>
            <div style={{width:22,height:22,borderRadius:"50%",background:BRAND.greenDark,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="11" height="11" viewBox="0 0 80 80" fill="none">
                <circle cx="40" cy="32" r="22" fill="#87c5d6"/>
                <path d="M22 20 Q28 14 38 18 Q44 22 42 30 Q38 36 32 34 Q24 30 22 20Z" fill="#fff"/>
                <path d="M46 18 Q56 16 58 26 Q58 34 52 38 Q46 40 44 34 Q42 26 46 18Z" fill="#fff"/>
              </svg>
            </div>
            <span style={{fontSize:12,fontWeight:700,color:BRAND.greenDark}}>Reply as Pangaea's HK</span>
          </div>
          <textarea
            value={replyText}
            onChange={e=>setReplyText(e.target.value)}
            rows={3}
            placeholder="Write your response — this will be visible to everyone under the post…"
            style={{width:"100%",padding:"10px 12px",borderRadius:8,border:`1.5px solid rgba(74,124,89,0.3)`,fontSize:13,fontFamily:"'DM Sans',sans-serif",color:BRAND.charcoal,background:BRAND.white,outline:"none",resize:"vertical",lineHeight:1.6}}
            onFocus={e=>e.target.style.borderColor=BRAND.green}
            onBlur={e=>e.target.style.borderColor="rgba(74,124,89,0.3)"}
          />
          <div style={{display:"flex",gap:8,marginTop:10,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowReplyBox(false)}
              style={{padding:"7px 16px",borderRadius:8,background:BRAND.cream,color:BRAND.muted,fontWeight:600,fontSize:13,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:"pointer"}}>
              Cancel
            </button>
            <button onClick={submitReply} disabled={savingReply||!replyText.trim()}
              style={{padding:"7px 16px",borderRadius:8,background:BRAND.greenDark,color:BRAND.white,fontWeight:600,fontSize:13,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:savingReply?"default":"pointer",opacity:savingReply||!replyText.trim()?0.6:1}}>
              {savingReply ? "Saving…" : "Post reply"}
            </button>
          </div>
        </div>
      )}

      {/* Like button */}
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <button onClick={()=>onLike(post.id)}
          style={{display:"flex",alignItems:"center",gap:5,padding:"5px 12px",borderRadius:8,border:`1px solid ${liked?BRAND.green:"rgba(74,124,89,0.2)"}`,background:liked?BRAND.green:"transparent",color:liked?BRAND.white:BRAND.muted,fontSize:12,fontWeight:600,cursor:"pointer",transition:"all 0.15s",fontFamily:"'DM Sans',sans-serif"}}>
          <span>{liked?"♥":"♡"}</span><span>{post.likes||0}</span>
        </button>
        {post.adminReply && !isAdmin && (
          <span style={{fontSize:11,color:BRAND.green,fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
            Team responded
          </span>
        )}
        {isAdmin && <span style={{fontSize:11,color:BRAND.muted,fontStyle:"italic"}}>Admin view</span>}
      </div>
    </div>
  );
}

function CommunityPage() {
  const [approvedPosts, setApprovedPosts] = useState([]);
  const [pendingPosts,  setPendingPosts]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [filter,     setFilter]     = useState("all");
  const [postType,   setPostType]   = useState("suggestion");
  const [name,       setName]       = useState("");
  const [body,       setBody]       = useState("");
  const [bodyError,  setBodyError]  = useState(false);
  const [toast,      setToast]      = useState(null);
  const [likedIds,   setLikedIds]   = useState(()=>JSON.parse(localStorage.getItem("phk_liked")||"[]"));
  const [isAdmin,    setIsAdmin]    = useState(false);
  const [showModal,  setShowModal]  = useState(false);

  const db = window.db;

  // Load approved posts
  // No orderBy in the query — where() + orderBy() requires a composite Firestore
  // index that doesn't auto-create, causing silent empty results. Sort client-side.
  useEffect(()=>{
    if (!db) { setLoading(false); return; }
    const { collection, onSnapshot, query, where } = window.firestore;
    const q = query(collection(db,"community_posts"), where("status","==","approved"));
    const unsub = onSnapshot(q, snap=>{
      const posts = snap.docs.map(d=>({id:d.id,...d.data()}));
      posts.sort((a,b)=>(b.ts||0)-(a.ts||0)); // newest first, client-side
      setApprovedPosts(posts);
      setLoading(false);
    });
    return ()=>unsub();
  },[]);

  // Load pending posts when admin unlocks
  // NOTE: No orderBy here — combining where() + orderBy() on different fields
  // requires a Firestore composite index which causes silent failures without one.
  // We sort client-side instead so no index setup is needed.
  useEffect(()=>{
    if (!isAdmin || !db) return;
    const { collection, onSnapshot, query, where } = window.firestore;
    const q = query(collection(db,"community_posts"), where("status","==","pending"));
    const unsub = onSnapshot(q, snap=>{
      const posts = snap.docs.map(d=>({id:d.id,...d.data()}));
      posts.sort((a,b)=>(a.ts||0)-(b.ts||0)); // oldest first, client-side
      setPendingPosts(posts);
    });
    return ()=>unsub();
  },[isAdmin]);

  const showToast = (msg, isError=false) => { setToast({msg,isError}); setTimeout(()=>setToast(null),3200); };

  const handleSubmit = async () => {
    if (!body.trim()) { setBodyError(true); return; }
    setBodyError(false); setSubmitting(true);
    try {
      const { collection, addDoc } = window.firestore;
      await addDoc(collection(db,"community_posts"),{
        name: name.trim()||"", body: body.trim(),
        type: postType, status:"pending", ts: Date.now(), likes:0,
      });
      setName(""); setBody(""); setPostType("suggestion");
      setSubmitted(true);
    } catch(e){ showToast("Could not submit — please try again.",true); }
    setSubmitting(false);
  };

  const handleApprove = async(id) => {
    try { const {doc,updateDoc}=window.firestore; await updateDoc(doc(db,"community_posts",id),{status:"approved"}); showToast("✓ Post approved and published."); }
    catch(e){ showToast("Could not approve.",true); }
  };

  const handleReject = async(id) => {
    try { const {doc,deleteDoc}=window.firestore; await deleteDoc(doc(db,"community_posts",id)); showToast("Post rejected and removed."); }
    catch(e){ showToast("Could not reject.",true); }
  };

  const handleDelete = async(id) => {
    try { const {doc,deleteDoc}=window.firestore; await deleteDoc(doc(db,"community_posts",id)); showToast("✓ Post deleted."); }
    catch(e){ showToast("Could not delete.",true); }
  };

  const handleLike = async(id) => {
    const already = likedIds.includes(id);
    const newLiked = already ? likedIds.filter(l=>l!==id) : [...likedIds,id];
    setLikedIds(newLiked); localStorage.setItem("phk_liked",JSON.stringify(newLiked));
    try { const {doc,updateDoc,increment}=window.firestore; await updateDoc(doc(db,"community_posts",id),{likes:increment(already?-1:1)}); } catch(e){}
  };

  const handleReply = async(id, replyText) => {
    try {
      const {doc,updateDoc} = window.firestore;
      // Empty string removes the reply, otherwise saves it
      await updateDoc(doc(db,"community_posts",id),{ adminReply: replyText });
      showToast(replyText ? "✓ Reply posted." : "Reply removed.");
    } catch(e) { showToast("Could not save reply — try again.",true); }
  };

  const filtered = filter==="all" ? approvedPosts : approvedPosts.filter(p=>p.type===filter);
  const inp = {width:"100%",padding:"11px 14px",borderRadius:9,border:"1.5px solid rgba(74,124,89,0.22)",fontSize:14,fontFamily:"'DM Sans',sans-serif",color:BRAND.charcoal,background:BRAND.white,outline:"none"};

  return (
    <div style={{paddingTop:90}}>
      {showModal && <AdminUnlockModal onClose={()=>setShowModal(false)} onUnlock={()=>{setIsAdmin(true);showToast("✓ Admin mode unlocked.");}}/>}

      <div style={{background:`linear-gradient(135deg,${BRAND.greenDark} 0%,${BRAND.green} 100%)`,padding:"56px clamp(16px,4vw,48px) 48px"}}>
        <div style={{maxWidth:740,margin:"0 auto",display:"flex",justifyContent:"space-between",alignItems:"flex-end",flexWrap:"wrap",gap:16}}>
          <div>
            <h1 style={{fontFamily:"'Playfair Display',serif",fontWeight:900,fontSize:"clamp(26px,4vw,42px)",color:BRAND.white,marginBottom:8}}>Community Board</h1>
            <p style={{color:"rgba(255,255,255,0.75)",fontSize:15,maxWidth:500}}>Share suggestions, reviews, or feedback. Posts are reviewed before appearing here.</p>
          </div>
          <button onClick={()=>isAdmin?setIsAdmin(false):setShowModal(true)}
            style={{padding:"8px 16px",borderRadius:9,background:isAdmin?"#fee2e2":"rgba(255,255,255,0.15)",border:isAdmin?`1.5px solid ${BRAND.danger}30`:"1.5px solid rgba(255,255,255,0.3)",color:isAdmin?BRAND.danger:"rgba(255,255,255,0.85)",fontSize:12,fontWeight:600,fontFamily:"'DM Sans',sans-serif",cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            {isAdmin?"Exit admin mode":"Admin"}
          </button>
        </div>
      </div>

      {isAdmin && (
        <div style={{background:"#fee2e2",borderBottom:`2px solid ${BRAND.danger}25`,padding:"10px clamp(16px,4vw,48px)"}}>
          <div style={{maxWidth:740,margin:"0 auto",fontSize:13,color:BRAND.danger,fontWeight:600,display:"flex",alignItems:"center",gap:8}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            Admin mode active — pending posts and moderation controls are visible.
            {pendingPosts.length>0 && <span style={{marginLeft:8,background:BRAND.danger,color:BRAND.white,fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20}}>{pendingPosts.length} pending</span>}
          </div>
        </div>
      )}

      <div style={{maxWidth:740,margin:"0 auto",padding:"36px clamp(16px,4vw,48px)"}}>

        {/* Admin pending queue */}
        {isAdmin && (
          <div style={{marginBottom:36}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
              <h2 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:19,color:BRAND.charcoal}}>Pending review</h2>
              {pendingPosts.length>0 && <span style={{background:BRAND.amber,color:BRAND.white,fontSize:12,fontWeight:700,padding:"2px 10px",borderRadius:20}}>{pendingPosts.length} awaiting</span>}
            </div>
            {pendingPosts.length===0
              ? <div style={{background:BRAND.cream,borderRadius:12,padding:"24px 20px",textAlign:"center",color:BRAND.muted,fontSize:14}}>No posts pending review. You're all caught up.</div>
              : pendingPosts.map(post=><PendingPostCard key={post.id} post={post} onApprove={()=>handleApprove(post.id)} onReject={()=>handleReject(post.id)}/>)
            }
            <div style={{height:1,background:"rgba(74,124,89,0.15)",margin:"28px 0"}}/>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:19,color:BRAND.charcoal,marginBottom:16}}>Published posts</h2>
          </div>
        )}

        {/* Submit form */}
        {!submitted ? (
          <div style={{background:BRAND.white,borderRadius:16,border:"1.5px solid rgba(74,124,89,0.2)",padding:"26px 30px",marginBottom:28}}>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:18,color:BRAND.charcoal,marginBottom:6}}>Leave a review or suggestion</h2>
            <p style={{fontSize:13,color:BRAND.muted,marginBottom:20,lineHeight:1.6}}>Posts are reviewed before being published. You'll typically see your post appear within 24 hours.</p>
            <div style={{marginBottom:14}}>
              <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:7}}>Your name (optional)</label>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Sarah M., or leave blank to post anonymously" style={inp}
                onFocus={e=>e.target.style.borderColor=BRAND.green} onBlur={e=>e.target.style.borderColor="rgba(74,124,89,0.22)"}/>
            </div>
            <div style={{marginBottom:14}}>
              <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:7}}>Post type</label>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {Object.entries(TYPE_CONFIG).map(([t,c])=>(
                  <button key={t} onClick={()=>setPostType(t)}
                    style={{padding:"6px 13px",borderRadius:20,fontSize:12,fontWeight:600,fontFamily:"'DM Sans',sans-serif",background:postType===t?BRAND.green:BRAND.white,color:postType===t?BRAND.white:BRAND.charcoal,border:`1.5px solid ${postType===t?BRAND.green:"rgba(74,124,89,0.22)"}`,cursor:"pointer",transition:"all 0.15s"}}>{c.label}</button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:6}}>
              <label style={{display:"block",fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",color:BRAND.muted,marginBottom:7}}>Your message <span style={{color:BRAND.danger}}>*</span></label>
              <textarea value={body} onChange={e=>{setBody(e.target.value);setBodyError(false);}} rows={3}
                placeholder="Share your thoughts, ideas, or feedback about Pangaea's HK…"
                style={{...inp,resize:"vertical",borderColor:bodyError?BRAND.danger:"rgba(74,124,89,0.22)"}}
                onFocus={e=>e.target.style.borderColor=bodyError?BRAND.danger:BRAND.green}
                onBlur={e=>e.target.style.borderColor=bodyError?BRAND.danger:"rgba(74,124,89,0.22)"}/>
            </div>
            {bodyError && <p style={{fontSize:12,color:BRAND.danger,fontWeight:500,marginBottom:10}}>Please write something before submitting.</p>}
            <div style={{display:"flex",justifyContent:"flex-end",marginTop:14}}>
              <button onClick={handleSubmit} disabled={submitting}
                style={{padding:"10px 24px",borderRadius:9,background:BRAND.green,color:BRAND.white,fontWeight:600,fontSize:14,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:submitting?"default":"pointer",opacity:submitting?0.6:1}}>
                {submitting?"Submitting…":"Submit for review"}
              </button>
            </div>
          </div>
        ) : (
          <div style={{background:"#e1f5ee",borderRadius:16,border:"1.5px solid #5DCAA5",padding:"28px 32px",marginBottom:28,textAlign:"center"}}>
            <div style={{width:52,height:52,borderRadius:"50%",background:BRAND.success+"20",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px"}}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={BRAND.success} strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <h3 style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:18,color:"#085041",marginBottom:8}}>Thanks — your post has been submitted!</h3>
            <p style={{fontSize:13,color:"#0f6e56",lineHeight:1.7,marginBottom:16}}>It will appear on the board once it's been reviewed. This usually takes less than 24 hours.</p>
            <button onClick={()=>setSubmitted(false)}
              style={{padding:"9px 22px",borderRadius:9,background:BRAND.success,color:BRAND.white,fontWeight:600,fontSize:13,fontFamily:"'DM Sans',sans-serif",border:"none",cursor:"pointer"}}>
              Submit another post
            </button>
          </div>
        )}

        {/* Filter row */}
        <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:20}}>
          {[["all","All"],["suggestion","💡 Suggestions"],["review","⭐ Reviews"],["bug","🐛 Bugs"],["other","💬 Other"]].map(([f,label])=>(
            <button key={f} onClick={()=>setFilter(f)}
              style={{padding:"6px 12px",borderRadius:20,fontSize:12,fontWeight:600,fontFamily:"'DM Sans',sans-serif",background:filter===f?BRAND.green:BRAND.white,color:filter===f?BRAND.white:BRAND.charcoal,border:`1.5px solid ${filter===f?BRAND.green:"rgba(74,124,89,0.2)"}`,cursor:"pointer",transition:"all 0.15s"}}>{label}</button>
          ))}
          {approvedPosts.length>0 && <span style={{marginLeft:"auto",fontSize:13,color:BRAND.muted,alignSelf:"center"}}>{filtered.length} post{filtered.length!==1?"s":""}</span>}
        </div>

        {loading && <div style={{textAlign:"center",padding:"48px 20px",color:BRAND.muted}}><Spinner size={28}/><div style={{fontSize:14,marginTop:12}}>Loading posts…</div></div>}

        {!loading && filtered.length===0 && (
          <div style={{textAlign:"center",padding:"48px 20px",color:BRAND.muted}}>
            <div style={{fontSize:36,marginBottom:12}}>{filter==="all"?"📝":"🔍"}</div>
            <p style={{fontSize:14}}>{filter==="all"?"No posts yet — be the first to leave a review or suggestion!":"No posts in this category yet."}</p>
          </div>
        )}

        {!loading && filtered.map(post=>(
          <ApprovedPostCard key={post.id} post={post} onLike={handleLike} liked={likedIds.includes(post.id)} isAdmin={isAdmin} onDelete={handleDelete} onReply={handleReply}/>
        ))}
      </div>

      {toast && (
        <div style={{position:"fixed",bottom:24,right:24,background:toast.isError?BRAND.danger:BRAND.success,color:BRAND.white,padding:"12px 18px",borderRadius:10,fontSize:13,fontWeight:500,fontFamily:"'DM Sans',sans-serif",zIndex:999,boxShadow:"0 4px 20px rgba(0,0,0,0.15)"}}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function UpdatesPage() {
  // ── CHANGELOG — add new entries at the TOP of this array ────
  // Copy any entry below, paste it at position [0], and fill in:
  //   date:    "Month DD, YYYY"
  //   version: optional label e.g. "v1.2" or "" to hide it
  //   title:   short headline for the update
  //   items:   array of bullet strings describing what changed
  //   type:    "new" | "fix" | "improvement" | "milestone"
  const CHANGELOG = [
    {
      date: "April 2026",
      version: "v1.0",
      type: "milestone",
      title: "Platform launched",
      items: [
        "Live trial search connected to ClinicalTrials.gov API v2 — 450,000+ real trials.",
        "17 disease categories including Cancer, Neurological, Rare Diseases, Allergy & Immunology, and Dental & Oral Health.",
        "Advanced filter panel: status, phase, sex, age range, country, intervention type, and results available.",
        "Side-by-side trial comparison for up to 4 trials simultaneously.",
        "Community board with admin moderation — posts reviewed before publishing.",
        "Admin reply system — team can respond publicly to community posts.",
        "How Trials Work — interactive 6-phase explainer from preclinical to post-market.",
        "Patient profile system with caregiver relationship field.",
        "About Us page with founder biography, social links, and trusted resource cards.",
        "Mobile-responsive navigation.",
        "Firebase Firestore backend for community board.",
      ],
    },
    // ── Paste new entries above this line ───────────────────────
    // {
    //   date: "May 2026",
    //   version: "",
    //   type: "new",
    //   title: "User accounts and saved trials",
    //   items: [
    //     "Email sign-up and login via Firebase Authentication.",
    //     "Saved trials persist across devices when signed in.",
    //   ],
    // },
  ];

  // ── ROADMAP — what's coming next ────────────────────────────
  const ROADMAP = [
    {
      horizon: "Next up",
      color: BRAND.green,
      bg: "#e1f5ee",
      items: [
        "Email sign-up and login — save trials and profiles permanently across devices.",
        "Disease category cards pre-fill the search page when clicked.",
        "Google Analytics added — helps us understand what users need most.",
        "Favicon (globe logo) in the browser tab.",
        "Contributors, Coming Soon!",
      ],
    },
    {
      horizon: "Coming soon",
      color: BRAND.amber,
      bg: BRAND.amber + "15",
      items: [
        "Trial follow system — get notified the moment a trial you're watching opens recruiting.",
        "Blog — condition spotlights and research summaries written by the PHK team.",
        "Researcher accounts — verified researchers can post updates to their trial pages.",
      ],
    },
    {
      horizon: "On the horizon",
      color: "#185fa5",
      bg: "#e6f1fb",
      items: [
        "Mobile apps for iOS and Android.",
        "Spanish and French language support.",
        "WHO ICTRP and EU Clinical Trials Register integration — non-US trials.",
        "PHK Foundation registered as a 501(c)(3) non-profit.",
      ],
    },
  ];

  const typeConfig = {
    milestone:   { label: "🏆 Milestone",   bg: BRAND.green,  text: BRAND.white },
    new:         { label: "✨ New",          bg: "#185fa5",    text: BRAND.white },
    improvement: { label: "⬆ Improvement", bg: BRAND.amber,   text: BRAND.white },
    fix:         { label: "🔧 Fix",          bg: BRAND.muted,  text: BRAND.white },
  };

  return (
    <div style={{ paddingTop: 90 }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${BRAND.greenDark} 0%, ${BRAND.green} 100%)`, padding: "56px clamp(16px,4vw,48px) 48px" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "clamp(28px,4vw,44px)", color: BRAND.white, marginBottom: 12 }}>
            Updates
          </h1>
          <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 15, maxWidth: 520, lineHeight: 1.75 }}>
            A running log of everything that has been changed, will be changed, or has already been launched that is pertinant to the functionality of the website.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: "0 auto", padding: "48px clamp(16px,4vw,48px) 80px" }}>

        {/* ── Roadmap ─────────────────────────────────────────── */}
        <div style={{ marginBottom: 56 }}>
          <div style={{ fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: BRAND.green, marginBottom: 10 }}>
            What's coming
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "clamp(20px,3vw,30px)", color: BRAND.charcoal, marginBottom: 28 }}>
            Roadmap
          </h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {ROADMAP.map(r => (
              <div key={r.horizon} style={{ background: BRAND.white, borderRadius: 14, border: `1px solid rgba(74,124,89,0.12)`, overflow: "hidden" }}>
                <div style={{ background: r.bg, padding: "10px 20px", display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: r.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: r.color }}>
                    {r.horizon}
                  </span>
                </div>
                <div style={{ padding: "16px 20px" }}>
                  {r.items.map((item, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: i < r.items.length - 1 ? 10 : 0 }}>
                      <div style={{ width: 5, height: 5, borderRadius: "50%", background: r.color, flexShrink: 0, marginTop: 7 }} />
                      <span style={{ fontSize: 13, color: BRAND.charcoal, lineHeight: 1.65 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Changelog ───────────────────────────────────────── */}
        <div>
          <div style={{ fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em", color: BRAND.green, marginBottom: 10 }}>
            History
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "clamp(20px,3vw,30px)", color: BRAND.charcoal, marginBottom: 28 }}>
            Changelog
          </h2>

          <div style={{ position: "relative" }}>
            {/* Vertical timeline line */}
            <div style={{ position: "absolute", left: 16, top: 8, bottom: 8, width: 2, background: `linear-gradient(to bottom, ${BRAND.green}, rgba(74,124,89,0.1))`, borderRadius: 2 }} />

            {CHANGELOG.map((entry, idx) => {
              const tc = typeConfig[entry.type] || typeConfig.new;
              return (
                <div key={idx} style={{ display: "flex", gap: 28, marginBottom: 36, position: "relative" }}>
                  {/* Timeline dot */}
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: tc.bg, border: `3px solid ${BRAND.cream}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1, marginTop: 2 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: BRAND.white, opacity: 0.9 }} />
                  </div>

                  {/* Entry card */}
                  <div style={{ flex: 1, background: BRAND.white, borderRadius: 14, border: `1px solid rgba(74,124,89,0.12)`, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                    {/* Card header */}
                    <div style={{ padding: "16px 20px 14px", borderBottom: `1px solid rgba(74,124,89,0.08)`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: tc.bg, color: tc.text }}>
                            {tc.label}
                          </span>
                          {entry.version && (
                            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: BRAND.cream, color: BRAND.muted }}>
                              {entry.version}
                            </span>
                          )}
                        </div>
                        <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 17, color: BRAND.charcoal, lineHeight: 1.3 }}>
                          {entry.title}
                        </h3>
                      </div>
                      <span style={{ fontSize: 12, color: BRAND.muted, fontWeight: 500, whiteSpace: "nowrap", marginTop: 4 }}>
                        {entry.date}
                      </span>
                    </div>

                    {/* Bullet list */}
                    <div style={{ padding: "14px 20px" }}>
                      {entry.items.map((item, i) => (
                        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: i < entry.items.length - 1 ? 9 : 0 }}>
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: BRAND.green, flexShrink: 0, marginTop: 7 }} />
                          <span style={{ fontSize: 13, color: BRAND.charcoal, lineHeight: 1.65 }}>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div style={{ background: BRAND.cream, borderRadius: 14, padding: "24px 28px", marginTop: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16, border: `1px solid rgba(74,124,89,0.12)` }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: BRAND.charcoal, marginBottom: 4 }}>Have a suggestion?</div>
            <div style={{ fontSize: 13, color: BRAND.muted }}>Tell us what you'd like to see — we read everything.</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <a href="mailto:hello@pangaeashk.org"
              style={{ padding: "9px 18px", borderRadius: 9, background: BRAND.green, color: BRAND.white, fontWeight: 600, fontSize: 13, fontFamily: "'DM Sans', sans-serif", textDecoration: "none" }}>
              Email us
            </a>
          </div>
        </div>

      </div>
    </div>
  );
}

function Footer({setPage}) {
  return (
    <footer style={{background:BRAND.charcoal,padding:"56px clamp(16px,4vw,48px) 30px"}}>
      <div style={{maxWidth:1200,margin:"0 auto"}}>
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:36,marginBottom:44}}>
          <div>
            <Logo size={34} textColor={BRAND.white}/>
            <p style={{fontSize:13,color:"rgba(255,255,255,0.4)",lineHeight:1.75,marginTop:14,maxWidth:260}}>Live trial data from ClinicalTrials.gov and global registries. Every card links directly to the official source.</p>
          </div>
          {[
            {heading:"Platform",links:[["Find Trials","search"],["Compare","compare"],["My Profile","profile"],["Community","community"],["Updates","updates"]]},
            {heading:"Organisation",links:[["About Us","about"],["Our Mission","about"],["Join the Team","about"]]},
            {heading:"Data sources",links:[["ClinicalTrials.gov",null],["WHO ICTRP",null],["EU CTR",null]]},
          ].map(col=>(
            <div key={col.heading}>
              <div style={{fontSize:11,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.1em",color:BRAND.green,marginBottom:14}}>{col.heading}</div>
              {col.links.map(([label,id])=>(
                <div key={label} style={{marginBottom:8}}>
                  {id
                    ?<button onClick={()=>setPage(id)} style={{fontSize:13,color:"rgba(255,255,255,0.5)",fontFamily:"'DM Sans',sans-serif"}}>{label}</button>
                    :<span style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>{label}</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{borderTop:"1px solid rgba(255,255,255,0.07)",paddingTop:22,display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:10}}>
          <span style={{fontSize:11,color:"rgba(255,255,255,0.25)"}}>© 2026 Pangaea's Health and Knowledge</span>
          <span style={{fontSize:11,color:"rgba(255,255,255,0.25)"}}>Not medical advice. Always consult a healthcare professional.</span>
        </div>
      </div>
    </footer>
  );
}
const TRIAL_PHASES = [
  {
    id: "preclinical",
    short: "Preclinical",
    icon: "🔬",
    iconBg: "#e1f5ee",
    tag: "Laboratory stage",
    tagBg: "#e1f5ee", tagColor: "#0f6e56",
    accentColor: "#1D9E75",
    title: "Preclinical research",
    subtitle: "Before any human is involved",
    body: "Scientists discover a potential treatment in the lab — often through studying disease biology at the cellular level. The treatment is first tested on cell cultures and animal models to understand how it works, whether it is effective, and whether it is safe enough to try in humans. This stage can take years, and most potential treatments never make it past here.",
    stats: [{ label: "Duration", value: "1–6 years" }, { label: "Human participants", value: "None" }, { label: "Success rate", value: "~1 in 5,000" }],
    details: ["Lab and animal testing only — no humans involved", "Researchers publish findings in scientific journals", "The FDA reviews data before approving human trials", "This is where most potential treatments are filtered out"],
    hope: "Even at this stage, a preclinical study means researchers are actively working on a treatment. Pangaea's HK tracks trials from Phase 1 onward — when preclinical results lead to a human trial, you'll see it here first.",
  },
  {
    id: "phase1",
    short: "Phase 1",
    icon: "👥",
    iconBg: "#e6f1fb",
    tag: "First in humans",
    tagBg: "#e6f1fb", tagColor: "#185fa5",
    accentColor: "#378ADD",
    title: "Phase 1 — safety testing",
    subtitle: "Is it safe enough in humans?",
    body: "The treatment is given to a small group of volunteers — often healthy adults, sometimes patients with the target disease — for the very first time. The goal is not to cure anyone yet; it is to understand how the human body processes the treatment, find the right dose, and identify any serious side effects.",
    stats: [{ label: "Participants", value: "20–80 people" }, { label: "Duration", value: "Months–1 year" }, { label: "Primary goal", value: "Safety & dosing" }],
    details: ["First time a treatment is tested in humans", "Participants monitored very closely for side effects", "Researchers find the safest effective dose range", "About 70% of treatments pass this phase"],
    hope: "A Phase 1 trial means a treatment has shown enough promise in the lab to be tried in humans. Even if you can't join yet, following a Phase 1 trial means you'll know the moment it advances to Phase 2.",
  },
  {
    id: "phase2",
    short: "Phase 2",
    icon: "📊",
    iconBg: "#faeeda",
    tag: "Does it work?",
    tagBg: "#faeeda", tagColor: "#633806",
    accentColor: "#BA7517",
    title: "Phase 2 — effectiveness testing",
    subtitle: "Does it actually help patients?",
    body: "With safety established, the treatment is now given to a larger group of patients who have the target disease. Researchers start measuring whether it actually works — does it reduce symptoms? Does it slow disease progression? This phase also continues monitoring for side effects in a more diverse group of people.",
    stats: [{ label: "Participants", value: "100–300 patients" }, { label: "Duration", value: "1–3 years" }, { label: "Primary goal", value: "Does it work?" }],
    details: ["Participants have the disease being studied", "Often uses a placebo or comparison group", "Measures whether the treatment improves outcomes", "About 33% of treatments pass to Phase 3"],
    hope: "Phase 2 is where real patients first receive the treatment. This is where Pangaea's HK matching becomes most useful — eligibility criteria are well-defined and recruiting is often active.",
  },
  {
    id: "phase3",
    short: "Phase 3",
    icon: "🌍",
    iconBg: "#faece7",
    tag: "Large-scale trial",
    tagBg: "#faece7", tagColor: "#712b13",
    accentColor: "#D85A30",
    title: "Phase 3 — large-scale confirmation",
    subtitle: "Is it better than what exists?",
    body: "This is the largest and most rigorous phase. Thousands of patients across multiple hospitals and countries are randomly assigned to receive either the new treatment or the current standard of care. The goal is to confirm that the treatment works, understand its long-term effects, and compare it directly against existing options.",
    stats: [{ label: "Participants", value: "300–3,000+" }, { label: "Duration", value: "1–4 years" }, { label: "Primary goal", value: "Better than standard?" }],
    details: ["Randomised controlled trial — the gold standard", "Multi-site: patients join at hospitals worldwide", "Some participants receive a placebo or existing treatment", "About 25–30% of treatments reach regulatory approval"],
    hope: "Phase 3 trials are the most likely to be open for recruitment. When you filter Pangaea's HK by 'recruiting now', many results will be Phase 3 trials where your participation could contribute to a treatment reaching approval.",
  },
  {
    id: "approval",
    short: "Regulatory review",
    icon: "🏛",
    iconBg: "#f1efe8",
    tag: "Regulatory review",
    tagBg: "#f1efe8", tagColor: "#444441",
    accentColor: "#888780",
    title: "Regulatory review",
    subtitle: "Examined by the FDA, EMA, or MHRA",
    body: "If Phase 3 results are positive, the manufacturer submits a full application to regulatory authorities — the FDA in the US, the EMA in Europe, the MHRA in the UK. Independent experts review all data from every phase. This process typically takes 6 months to 2 years, and the regulator may approve, reject, or request additional studies.",
    stats: [{ label: "Duration", value: "6 months–2 years" }, { label: "Who reviews", value: "FDA / EMA / MHRA" }, { label: "Outcome", value: "Approve or reject" }],
    details: ["All trial data submitted in a New Drug Application", "Independent experts review safety and efficacy", "Accelerated pathways exist for urgent or rare disease treatments", "Approval may be conditional on ongoing monitoring"],
    hope: "Treatments for serious conditions can qualify for Breakthrough Therapy Designation, which speeds up FDA review. Pangaea's HK tracks these designations so you can follow the most promising treatments.",
  },
  {
    id: "phase4",
    short: "Phase 4",
    icon: "🔭",
    iconBg: "#eeedfe",
    tag: "Post-approval",
    tagBg: "#eeedfe", tagColor: "#3C3489",
    accentColor: "#534AB7",
    title: "Phase 4 — post-market surveillance",
    subtitle: "Long-term monitoring after approval",
    body: "After a treatment is approved, research continues. Phase 4 studies monitor long-term safety and effectiveness across millions of real-world patients over many years. This phase can reveal rare side effects too uncommon to appear in smaller trials, and can discover new uses for existing treatments.",
    stats: [{ label: "Participants", value: "Thousands–millions" }, { label: "Duration", value: "Years–decades" }, { label: "Primary goal", value: "Long-term safety" }],
    details: ["Treatment is already approved and available", "Monitors real-world use across diverse populations", "Can lead to new approved uses for the treatment", "Patient registries and real-world data are key sources"],
    hope: "Phase 4 research often expands access to new patient groups. Many Phase 4 studies recruit participants broadly — sometimes people who couldn't join earlier, more restrictive phases.",
  },
];

function HowTrialsWorkPage({ setPage }) {
  const [current, setCurrent] = useState(0);
  const p = TRIAL_PHASES[current];

  const trackColors = TRIAL_PHASES.map(ph => ph.accentColor);

  return (
    <div style={{ paddingTop: 90 }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${BRAND.greenDark} 0%, ${BRAND.green} 100%)`, padding: "56px clamp(16px,4vw,48px) 48px", textAlign: "center" }}>
        <h1 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900, fontSize: "clamp(26px,4vw,42px)", color: BRAND.white, marginBottom: 10 }}>
          How a Clinical Trial Works
        </h1>
        <p style={{ color: "rgba(255,255,255,0.78)", fontSize: 15, maxWidth: 520, margin: "0 auto" }}>
          From the first lab experiment to a treatment reaching patients — every phase explained in plain language. Click through each stage to learn what it means and how it affects you.
        </p>
      </div>

      <div style={{ maxWidth: 820, margin: "0 auto", padding: "40px clamp(16px,4vw,48px) 64px" }}>

        {/* Phase track */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: 32, overflowX: "auto", paddingBottom: 8 }}>
          {TRIAL_PHASES.map((ph, i) => (
            <div key={ph.id} style={{ display: "flex", alignItems: "center", flex: i < TRIAL_PHASES.length - 1 ? "1 1 0" : "0 0 auto" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer", minWidth: 64 }} onClick={() => setCurrent(i)}>
                <div style={{
                  width: 44, height: 44, borderRadius: "50%",
                  background: i < current ? BRAND.green : i === current ? ph.accentColor : BRAND.cream,
                  border: `2px solid ${i === current ? ph.accentColor : i < current ? BRAND.green : "rgba(74,124,89,0.2)"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 13, fontWeight: 700,
                  color: i <= current ? BRAND.white : BRAND.muted,
                  transition: "all 0.2s",
                }}>
                  {i < current
                    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    : <span style={{ fontSize: 11 }}>{["Pre", "P1", "P2", "P3", "Rev", "P4"][i]}</span>
                  }
                </div>
                <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", color: i === current ? BRAND.green : BRAND.muted, textAlign: "center", lineHeight: 1.3, maxWidth: 56 }}>
                  {ph.short}
                </span>
              </div>
              {i < TRIAL_PHASES.length - 1 && (
                <div style={{ flex: 1, height: 2, background: i < current ? BRAND.green : "rgba(74,124,89,0.15)", margin: "0 4px 22px", transition: "background 0.3s", minWidth: 16 }} />
              )}
            </div>
          ))}
        </div>

        {/* Phase card */}
        <div key={p.id} className="fade-in" style={{ background: BRAND.white, borderRadius: 18, padding: "32px 34px", border: `1.5px solid ${p.accentColor}30`, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 22 }}>
            <div style={{ width: 56, height: 56, borderRadius: 16, background: p.iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0 }}>
              {p.icon}
            </div>
            <div>
              <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", background: p.tagBg, color: p.tagColor, marginBottom: 6 }}>
                {p.tag}
              </span>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 22, color: BRAND.charcoal, marginBottom: 4, lineHeight: 1.25 }}>{p.title}</h2>
              <p style={{ fontSize: 14, color: BRAND.muted }}>{p.subtitle}</p>
            </div>
          </div>

          <p style={{ fontSize: 14, color: BRAND.charcoal, lineHeight: 1.8, marginBottom: 22 }}>{p.body}</p>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 22 }}>
            {p.stats.map(s => (
              <div key={s.label} style={{ background: BRAND.cream, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: BRAND.muted, marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: BRAND.charcoal }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 20 }}>
            {p.details.map((d, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: BRAND.charcoal, lineHeight: 1.6 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: p.accentColor, flexShrink: 0, marginTop: 6 }} />
                {d}
              </div>
            ))}
          </div>

          <div style={{ background: "#e1f5ee", border: `1px solid #5DCAA5`, borderRadius: 12, padding: "14px 18px", fontSize: 13, color: "#085041", lineHeight: 1.65 }}>
            {p.hope}
          </div>

          {/* Nav */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24 }}>
            <button onClick={() => setCurrent(c => Math.max(0, c - 1))} disabled={current === 0}
              style={{ padding: "9px 20px", borderRadius: 9, border: `1.5px solid rgba(74,124,89,0.3)`, background: "transparent", color: BRAND.green, fontWeight: 600, fontSize: 13, fontFamily: "'DM Sans', sans-serif", cursor: current === 0 ? "default" : "pointer", opacity: current === 0 ? 0.35 : 1 }}
            >← Previous</button>
            <span style={{ fontSize: 12, color: BRAND.muted }}>{current + 1} of {TRIAL_PHASES.length}</span>
            {current < TRIAL_PHASES.length - 1
              ? <button onClick={() => setCurrent(c => c + 1)} style={{ padding: "9px 20px", borderRadius: 9, background: BRAND.greenDark, color: BRAND.white, fontWeight: 600, fontSize: 13, fontFamily: "'DM Sans', sans-serif", border: "none", cursor: "pointer" }}>Next →</button>
              : <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} style={{ padding: "9px 20px", borderRadius: 9, background: BRAND.greenDark, color: BRAND.white, fontWeight: 600, fontSize: 13, fontFamily: "'DM Sans', sans-serif", border: "none", cursor: "pointer" }}>Back to top ↑</button>
            }
          </div>
        </div>

        {/* CTA to search */}
        <div style={{ background: BRAND.charcoal, borderRadius: 16, padding: "28px 32px", textAlign: "center" }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 20, color: BRAND.white, marginBottom: 8 }}>
            Ready to find a trial?
          </h3>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", marginBottom: 20, lineHeight: 1.7 }}>
            Now that you understand the process, search our database of real, live trials. Every result links directly to its source registry.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              style={{ padding: "12px 28px", borderRadius: 10, background: BRAND.green, color: BRAND.white, fontWeight: 700, fontSize: 14, fontFamily: "'DM Sans', sans-serif", border: "none", cursor: "pointer" }}
              onClick={() => setPage("search")}
            >
              Find trials →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [page, setPage] = useState("home");
  const [comparison, setComparison] = useState([]);
  const [detailTrial, setDetailTrial] = useState(null);

  // Check if already unlocked this session (survives page refresh)
  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem("phk_unlocked") === "true"
  );

  useEffect(() => { window.scrollTo(0, 0); }, [page]);

  // Show password gate until correct password is entered
  if (!unlocked) {
    return <PasswordGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <>
      <FontStyle />
      <Nav page={page} setPage={setPage} />
      <main style={{ minHeight: "100vh" }}>
        {page === "home"    && <HomePage setPage={setPage} />}
        {page === "search"  && <SearchPage comparison={comparison} setComparison={setComparison} setPage={setPage} setDetailTrial={setDetailTrial} />}
        {page === "detail"  && <DetailPage trial={detailTrial} setPage={setPage} />}
        {page === "compare" && <ComparePage comparison={comparison} setComparison={setComparison} setPage={setPage} setDetailTrial={setDetailTrial} />}
        {page === "profile" && <ProfilePage />}
        {page === "about"   && <AboutPage />}
        {page === "updates" && <UpdatesPage />}
        {page === "how-trials-work" && <HowTrialsWorkPage setPage={setPage} />}
        {page === "community" && <CommunityPage />}
      </main>
      <Footer setPage={setPage} />
    </>
  );
}
